import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import DateRangeSetter from '@amplify/date-range-setter';
import styled from 'styled-components';

function _iterableToArrayLimit(arr, i) {
  var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"];
  if (null != _i) {
    var _s,
      _e,
      _x,
      _r,
      _arr = [],
      _n = !0,
      _d = !1;
    try {
      if (_x = (_i = _i.call(arr)).next, 0 === i) {
        if (Object(_i) !== _i) return;
        _n = !1;
      } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0);
    } catch (err) {
      _d = !0, _e = err;
    } finally {
      try {
        if (!_n && null != _i.return && (_r = _i.return(), Object(_r) !== _r)) return;
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
}
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var get24HourFrom12Hour = function get24HourFrom12Hour(hour, isAM) {
  if (typeof hour === 'string') {
    hour = parseInt(hour);
  }
  if (hour === 12) {
    return isAM ? 0 : 12;
  }
  return isAM ? hour : 12 + hour;
};
var getCurrentPeriod = function getCurrentPeriod(isAM) {
  return isAM ? 'am' : 'pm';
};
var get12HourFrom24Hour = function get12HourFrom24Hour(hour) {
  var isAM = getIsAM(hour);
  var isMidnight = getIsMidnight(hour);
  var isNoon = getIsNoon(hour);
  return isMidnight || isNoon ? 12 : isAM ? hour : hour - 12;
};
var getIsAM = function getIsAM(twentyfourHourOrPeriod) {
  if (typeof twentyfourHourOrPeriod === 'number') {
    return twentyfourHourOrPeriod < 12;
  } else {
    return (twentyfourHourOrPeriod === null || twentyfourHourOrPeriod === void 0 ? void 0 : twentyfourHourOrPeriod.toLowerCase()) === 'am';
  }
};
var getIsMidnight = function getIsMidnight(hour) {
  return hour === 0;
};
var getIsNoon = function getIsNoon(hour) {
  return hour === 12;
};
var getIsValidPeriod = function getIsValidPeriod(period) {
  return (period === null || period === void 0 ? void 0 : period.toLowerCase()) === 'am' || (period === null || period === void 0 ? void 0 : period.toLowerCase()) === 'pm';
};
var padTime = function padTime(string) {
  var padding = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
  return string.toString().padStart(padding, '0');
};

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

function buildLocalizeFn(args) {
  return function (dirtyIndex, options) {
    var context = options !== null && options !== void 0 && options.context ? String(options.context) : 'standalone';
    var valuesArray;
    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options !== null && options !== void 0 && options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;
      var _width = options !== null && options !== void 0 && options.width ? String(options.width) : args.defaultWidth;
      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }
    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex;
    // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!
    return valuesArray[index];
  };
}

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);
    if (!matchResult) {
      return null;
    }
    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}
function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }
  return undefined;
}
function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return undefined;
}

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof(obj);
}

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }
  var number = Number(dirtyNumber);
  if (isNaN(number)) {
    return number;
  }
  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */
function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument);

  // Clone the date
  if (argument instanceof Date || _typeof(argument) === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments");
      // eslint-disable-next-line no-console
      console.warn(new Error().stack);
    }
    return new Date(NaN);
  }
}

var defaultOptions = {};
function getDefaultOptions() {
  return defaultOptions;
}

/**
 * Days in 1 week.
 *
 * @name daysInWeek
 * @constant
 * @type {number}
 * @default
 */

/**
 * Milliseconds in 1 minute
 *
 * @name millisecondsInMinute
 * @constant
 * @type {number}
 * @default
 */
var millisecondsInMinute = 60000;

/**
 * Milliseconds in 1 hour
 *
 * @name millisecondsInHour
 * @constant
 * @type {number}
 * @default
 */
var millisecondsInHour = 3600000;

/**
 * Milliseconds in 1 second
 *
 * @name millisecondsInSecond
 * @constant
 * @type {number}
 * @default
 */
var millisecondsInSecond = 1000;

function startOfUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function getUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);
  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);
  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

function startOfUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date;
}

var MILLISECONDS_IN_WEEK = 604800000;
function getUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime();

  // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)
  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1;
}

function startOfUTCWeek(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$weekStartsOn, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var weekStartsOn = toInteger((_ref = (_ref2 = (_ref3 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.weekStartsOn) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.weekStartsOn) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.weekStartsOn) !== null && _ref !== void 0 ? _ref : 0);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function getUTCWeekYear(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$firstWeekCon, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var defaultOptions = getDefaultOptions();
  var firstWeekContainsDate = toInteger((_ref = (_ref2 = (_ref3 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref !== void 0 ? _ref : 1);

  // Test if weekStartsOn is between 1 and 7 _and_ is not NaN
  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }
  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, options);
  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, options);
  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

function startOfUTCWeekYear(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$firstWeekCon, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var firstWeekContainsDate = toInteger((_ref = (_ref2 = (_ref3 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref !== void 0 ? _ref : 1);
  var year = getUTCWeekYear(dirtyDate, options);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, options);
  return date;
}

var MILLISECONDS_IN_WEEK$1 = 604800000;
function getUTCWeek(dirtyDate, options) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCWeek(date, options).getTime() - startOfUTCWeekYear(date, options).getTime();

  // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)
  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1;
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },
  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },
  halfAMinute: 'half a minute',
  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },
  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },
  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },
  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },
  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },
  aboutXWeeks: {
    one: 'about 1 week',
    other: 'about {{count}} weeks'
  },
  xWeeks: {
    one: '1 week',
    other: '{{count}} weeks'
  },
  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },
  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },
  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },
  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },
  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },
  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};
var formatDistance = function formatDistance(token, count, options) {
  var result;
  var tokenValue = formatDistanceLocale[token];
  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', count.toString());
  }
  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }
  return result;
};

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  "long": 'MMMM do, y',
  medium: 'MMM d, y',
  "short": 'MM/dd/yyyy'
};
var timeFormats = {
  full: 'h:mm:ss a zzzz',
  "long": 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  "short": 'h:mm a'
};
var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  "long": "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  "short": '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};
var formatRelative = function formatRelative(token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
};

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter']
};

// Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.
var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  "short": ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};
var ordinalNumber = function ordinalNumber(dirtyNumber, _options) {
  var number = Number(dirtyNumber);

  // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  var rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st';
      case 2:
        return number + 'nd';
      case 3:
        return number + 'rd';
    }
  }
  return number + 'th';
};
var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function argumentCallback(quarter) {
      return quarter - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  "short": /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function valueCallback(value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function valueCallback(index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */
var locale = {
  code: 'en-US',
  formatDistance: formatDistance,
  formatLong: formatLong,
  formatRelative: formatRelative,
  localize: localize,
  match: match,
  options: {
    weekStartsOn: 0 /* Sunday */,
    firstWeekContainsDate: 1
  }
};

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return self;
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };
  return _setPrototypeOf(o, p);
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  Object.defineProperty(subClass, "prototype", {
    writable: false
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;
  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _possibleConstructorReturn(self, call) {
  if (call && (_typeof(call) === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }
  return _assertThisInitialized(self);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();
  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
      result;
    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;
      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }
    return _possibleConstructorReturn(this, result);
  };
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _toPrimitive(input, hint) {
  if (_typeof(input) !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (_typeof(res) !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return _typeof(key) === "symbol" ? key : String(key);
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var Setter = /*#__PURE__*/function () {
  function Setter() {
    _classCallCheck(this, Setter);
    _defineProperty(this, "priority", void 0);
    _defineProperty(this, "subPriority", 0);
  }
  _createClass(Setter, [{
    key: "validate",
    value: function validate(_utcDate, _options) {
      return true;
    }
  }]);
  return Setter;
}();
var ValueSetter = /*#__PURE__*/function (_Setter) {
  _inherits(ValueSetter, _Setter);
  var _super = _createSuper(ValueSetter);
  function ValueSetter(value, validateValue, setValue, priority, subPriority) {
    var _this;
    _classCallCheck(this, ValueSetter);
    _this = _super.call(this);
    _this.value = value;
    _this.validateValue = validateValue;
    _this.setValue = setValue;
    _this.priority = priority;
    if (subPriority) {
      _this.subPriority = subPriority;
    }
    return _this;
  }
  _createClass(ValueSetter, [{
    key: "validate",
    value: function validate(utcDate, options) {
      return this.validateValue(utcDate, this.value, options);
    }
  }, {
    key: "set",
    value: function set(utcDate, flags, options) {
      return this.setValue(utcDate, flags, this.value, options);
    }
  }]);
  return ValueSetter;
}(Setter);

var Parser = /*#__PURE__*/function () {
  function Parser() {
    _classCallCheck(this, Parser);
    _defineProperty(this, "incompatibleTokens", void 0);
    _defineProperty(this, "priority", void 0);
    _defineProperty(this, "subPriority", void 0);
  }
  _createClass(Parser, [{
    key: "run",
    value: function run(dateString, token, match, options) {
      var result = this.parse(dateString, token, match, options);
      if (!result) {
        return null;
      }
      return {
        setter: new ValueSetter(result.value, this.validate, this.set, this.priority, this.subPriority),
        rest: result.rest
      };
    }
  }, {
    key: "validate",
    value: function validate(_utcDate, _value, _options) {
      return true;
    }
  }]);
  return Parser;
}();

var EraParser = /*#__PURE__*/function (_Parser) {
  _inherits(EraParser, _Parser);
  var _super = _createSuper(EraParser);
  function EraParser() {
    var _this;
    _classCallCheck(this, EraParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 140);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['R', 'u', 't', 'T']);
    return _this;
  }
  _createClass(EraParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // AD, BC
        case 'G':
        case 'GG':
        case 'GGG':
          return match.era(dateString, {
            width: 'abbreviated'
          }) || match.era(dateString, {
            width: 'narrow'
          });
        // A, B
        case 'GGGGG':
          return match.era(dateString, {
            width: 'narrow'
          });
        // Anno Domini, Before Christ
        case 'GGGG':
        default:
          return match.era(dateString, {
            width: 'wide'
          }) || match.era(dateString, {
            width: 'abbreviated'
          }) || match.era(dateString, {
            width: 'narrow'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      flags.era = value;
      date.setUTCFullYear(value, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return EraParser;
}(Parser);

var numericPatterns = {
  month: /^(1[0-2]|0?\d)/,
  // 0 to 12
  date: /^(3[0-1]|[0-2]?\d)/,
  // 0 to 31
  dayOfYear: /^(36[0-6]|3[0-5]\d|[0-2]?\d?\d)/,
  // 0 to 366
  week: /^(5[0-3]|[0-4]?\d)/,
  // 0 to 53
  hour23h: /^(2[0-3]|[0-1]?\d)/,
  // 0 to 23
  hour24h: /^(2[0-4]|[0-1]?\d)/,
  // 0 to 24
  hour11h: /^(1[0-1]|0?\d)/,
  // 0 to 11
  hour12h: /^(1[0-2]|0?\d)/,
  // 0 to 12
  minute: /^[0-5]?\d/,
  // 0 to 59
  second: /^[0-5]?\d/,
  // 0 to 59

  singleDigit: /^\d/,
  // 0 to 9
  twoDigits: /^\d{1,2}/,
  // 0 to 99
  threeDigits: /^\d{1,3}/,
  // 0 to 999
  fourDigits: /^\d{1,4}/,
  // 0 to 9999

  anyDigitsSigned: /^-?\d+/,
  singleDigitSigned: /^-?\d/,
  // 0 to 9, -0 to -9
  twoDigitsSigned: /^-?\d{1,2}/,
  // 0 to 99, -0 to -99
  threeDigitsSigned: /^-?\d{1,3}/,
  // 0 to 999, -0 to -999
  fourDigitsSigned: /^-?\d{1,4}/ // 0 to 9999, -0 to -9999
};

var timezonePatterns = {
  basicOptionalMinutes: /^([+-])(\d{2})(\d{2})?|Z/,
  basic: /^([+-])(\d{2})(\d{2})|Z/,
  basicOptionalSeconds: /^([+-])(\d{2})(\d{2})((\d{2}))?|Z/,
  extended: /^([+-])(\d{2}):(\d{2})|Z/,
  extendedOptionalSeconds: /^([+-])(\d{2}):(\d{2})(:(\d{2}))?|Z/
};

function mapValue(parseFnResult, mapFn) {
  if (!parseFnResult) {
    return parseFnResult;
  }
  return {
    value: mapFn(parseFnResult.value),
    rest: parseFnResult.rest
  };
}
function parseNumericPattern(pattern, dateString) {
  var matchResult = dateString.match(pattern);
  if (!matchResult) {
    return null;
  }
  return {
    value: parseInt(matchResult[0], 10),
    rest: dateString.slice(matchResult[0].length)
  };
}
function parseTimezonePattern(pattern, dateString) {
  var matchResult = dateString.match(pattern);
  if (!matchResult) {
    return null;
  }

  // Input is 'Z'
  if (matchResult[0] === 'Z') {
    return {
      value: 0,
      rest: dateString.slice(1)
    };
  }
  var sign = matchResult[1] === '+' ? 1 : -1;
  var hours = matchResult[2] ? parseInt(matchResult[2], 10) : 0;
  var minutes = matchResult[3] ? parseInt(matchResult[3], 10) : 0;
  var seconds = matchResult[5] ? parseInt(matchResult[5], 10) : 0;
  return {
    value: sign * (hours * millisecondsInHour + minutes * millisecondsInMinute + seconds * millisecondsInSecond),
    rest: dateString.slice(matchResult[0].length)
  };
}
function parseAnyDigitsSigned(dateString) {
  return parseNumericPattern(numericPatterns.anyDigitsSigned, dateString);
}
function parseNDigits(n, dateString) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigit, dateString);
    case 2:
      return parseNumericPattern(numericPatterns.twoDigits, dateString);
    case 3:
      return parseNumericPattern(numericPatterns.threeDigits, dateString);
    case 4:
      return parseNumericPattern(numericPatterns.fourDigits, dateString);
    default:
      return parseNumericPattern(new RegExp('^\\d{1,' + n + '}'), dateString);
  }
}
function parseNDigitsSigned(n, dateString) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigitSigned, dateString);
    case 2:
      return parseNumericPattern(numericPatterns.twoDigitsSigned, dateString);
    case 3:
      return parseNumericPattern(numericPatterns.threeDigitsSigned, dateString);
    case 4:
      return parseNumericPattern(numericPatterns.fourDigitsSigned, dateString);
    default:
      return parseNumericPattern(new RegExp('^-?\\d{1,' + n + '}'), dateString);
  }
}
function dayPeriodEnumToHours(dayPeriod) {
  switch (dayPeriod) {
    case 'morning':
      return 4;
    case 'evening':
      return 17;
    case 'pm':
    case 'noon':
    case 'afternoon':
      return 12;
    case 'am':
    case 'midnight':
    case 'night':
    default:
      return 0;
  }
}
function normalizeTwoDigitYear(twoDigitYear, currentYear) {
  var isCommonEra = currentYear > 0;
  // Absolute number of the current year:
  // 1 -> 1 AC
  // 0 -> 1 BC
  // -1 -> 2 BC
  var absCurrentYear = isCommonEra ? currentYear : 1 - currentYear;
  var result;
  if (absCurrentYear <= 50) {
    result = twoDigitYear || 100;
  } else {
    var rangeEnd = absCurrentYear + 50;
    var rangeEndCentury = Math.floor(rangeEnd / 100) * 100;
    var isPreviousCentury = twoDigitYear >= rangeEnd % 100;
    result = twoDigitYear + rangeEndCentury - (isPreviousCentury ? 100 : 0);
  }
  return isCommonEra ? result : 1 - result;
}
function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100 !== 0;
}

// From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_Patterns
// | Year     |     y | yy |   yyy |  yyyy | yyyyy |
// |----------|-------|----|-------|-------|-------|
// | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
// | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
// | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
// | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
// | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
var YearParser = /*#__PURE__*/function (_Parser) {
  _inherits(YearParser, _Parser);
  var _super = _createSuper(YearParser);
  function YearParser() {
    var _this;
    _classCallCheck(this, YearParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 130);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['Y', 'R', 'u', 'w', 'I', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(YearParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(year) {
        return {
          year: year,
          isTwoDigitYear: token === 'yy'
        };
      };
      switch (token) {
        case 'y':
          return mapValue(parseNDigits(4, dateString), valueCallback);
        case 'yo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'year'
          }), valueCallback);
        default:
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value.isTwoDigitYear || value.year > 0;
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      var currentYear = date.getUTCFullYear();
      if (value.isTwoDigitYear) {
        var normalizedTwoDigitYear = normalizeTwoDigitYear(value.year, currentYear);
        date.setUTCFullYear(normalizedTwoDigitYear, 0, 1);
        date.setUTCHours(0, 0, 0, 0);
        return date;
      }
      var year = !('era' in flags) || flags.era === 1 ? value.year : 1 - value.year;
      date.setUTCFullYear(year, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return YearParser;
}(Parser);

// Local week-numbering year
var LocalWeekYearParser = /*#__PURE__*/function (_Parser) {
  _inherits(LocalWeekYearParser, _Parser);
  var _super = _createSuper(LocalWeekYearParser);
  function LocalWeekYearParser() {
    var _this;
    _classCallCheck(this, LocalWeekYearParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 130);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['y', 'R', 'u', 'Q', 'q', 'M', 'L', 'I', 'd', 'D', 'i', 't', 'T']);
    return _this;
  }
  _createClass(LocalWeekYearParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(year) {
        return {
          year: year,
          isTwoDigitYear: token === 'YY'
        };
      };
      switch (token) {
        case 'Y':
          return mapValue(parseNDigits(4, dateString), valueCallback);
        case 'Yo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'year'
          }), valueCallback);
        default:
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value.isTwoDigitYear || value.year > 0;
    }
  }, {
    key: "set",
    value: function set(date, flags, value, options) {
      var currentYear = getUTCWeekYear(date, options);
      if (value.isTwoDigitYear) {
        var normalizedTwoDigitYear = normalizeTwoDigitYear(value.year, currentYear);
        date.setUTCFullYear(normalizedTwoDigitYear, 0, options.firstWeekContainsDate);
        date.setUTCHours(0, 0, 0, 0);
        return startOfUTCWeek(date, options);
      }
      var year = !('era' in flags) || flags.era === 1 ? value.year : 1 - value.year;
      date.setUTCFullYear(year, 0, options.firstWeekContainsDate);
      date.setUTCHours(0, 0, 0, 0);
      return startOfUTCWeek(date, options);
    }
  }]);
  return LocalWeekYearParser;
}(Parser);

var ISOWeekYearParser = /*#__PURE__*/function (_Parser) {
  _inherits(ISOWeekYearParser, _Parser);
  var _super = _createSuper(ISOWeekYearParser);
  function ISOWeekYearParser() {
    var _this;
    _classCallCheck(this, ISOWeekYearParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 130);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['G', 'y', 'Y', 'u', 'Q', 'q', 'M', 'L', 'w', 'd', 'D', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(ISOWeekYearParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      if (token === 'R') {
        return parseNDigitsSigned(4, dateString);
      }
      return parseNDigitsSigned(token.length, dateString);
    }
  }, {
    key: "set",
    value: function set(_date, _flags, value) {
      var firstWeekOfYear = new Date(0);
      firstWeekOfYear.setUTCFullYear(value, 0, 4);
      firstWeekOfYear.setUTCHours(0, 0, 0, 0);
      return startOfUTCISOWeek(firstWeekOfYear);
    }
  }]);
  return ISOWeekYearParser;
}(Parser);

var ExtendedYearParser = /*#__PURE__*/function (_Parser) {
  _inherits(ExtendedYearParser, _Parser);
  var _super = _createSuper(ExtendedYearParser);
  function ExtendedYearParser() {
    var _this;
    _classCallCheck(this, ExtendedYearParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 130);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['G', 'y', 'Y', 'R', 'w', 'I', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(ExtendedYearParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      if (token === 'u') {
        return parseNDigitsSigned(4, dateString);
      }
      return parseNDigitsSigned(token.length, dateString);
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCFullYear(value, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return ExtendedYearParser;
}(Parser);

var QuarterParser = /*#__PURE__*/function (_Parser) {
  _inherits(QuarterParser, _Parser);
  var _super = _createSuper(QuarterParser);
  function QuarterParser() {
    var _this;
    _classCallCheck(this, QuarterParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 120);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['Y', 'R', 'q', 'M', 'L', 'w', 'I', 'd', 'D', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(QuarterParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // 1, 2, 3, 4
        case 'Q':
        case 'QQ':
          // 01, 02, 03, 04
          return parseNDigits(token.length, dateString);
        // 1st, 2nd, 3rd, 4th
        case 'Qo':
          return match.ordinalNumber(dateString, {
            unit: 'quarter'
          });
        // Q1, Q2, Q3, Q4
        case 'QQQ':
          return match.quarter(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // 1, 2, 3, 4 (narrow quarter; could be not numerical)
        case 'QQQQQ':
          return match.quarter(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // 1st quarter, 2nd quarter, ...
        case 'QQQQ':
        default:
          return match.quarter(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.quarter(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 4;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth((value - 1) * 3, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return QuarterParser;
}(Parser);

var StandAloneQuarterParser = /*#__PURE__*/function (_Parser) {
  _inherits(StandAloneQuarterParser, _Parser);
  var _super = _createSuper(StandAloneQuarterParser);
  function StandAloneQuarterParser() {
    var _this;
    _classCallCheck(this, StandAloneQuarterParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 120);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['Y', 'R', 'Q', 'M', 'L', 'w', 'I', 'd', 'D', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(StandAloneQuarterParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // 1, 2, 3, 4
        case 'q':
        case 'qq':
          // 01, 02, 03, 04
          return parseNDigits(token.length, dateString);
        // 1st, 2nd, 3rd, 4th
        case 'qo':
          return match.ordinalNumber(dateString, {
            unit: 'quarter'
          });
        // Q1, Q2, Q3, Q4
        case 'qqq':
          return match.quarter(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // 1, 2, 3, 4 (narrow quarter; could be not numerical)
        case 'qqqqq':
          return match.quarter(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // 1st quarter, 2nd quarter, ...
        case 'qqqq':
        default:
          return match.quarter(dateString, {
            width: 'wide',
            context: 'standalone'
          }) || match.quarter(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 4;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth((value - 1) * 3, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return StandAloneQuarterParser;
}(Parser);

var MonthParser = /*#__PURE__*/function (_Parser) {
  _inherits(MonthParser, _Parser);
  var _super = _createSuper(MonthParser);
  function MonthParser() {
    var _this;
    _classCallCheck(this, MonthParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'L', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']);
    _defineProperty(_assertThisInitialized(_this), "priority", 110);
    return _this;
  }
  _createClass(MonthParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(value) {
        return value - 1;
      };
      switch (token) {
        // 1, 2, ..., 12
        case 'M':
          return mapValue(parseNumericPattern(numericPatterns.month, dateString), valueCallback);
        // 01, 02, ..., 12
        case 'MM':
          return mapValue(parseNDigits(2, dateString), valueCallback);
        // 1st, 2nd, ..., 12th
        case 'Mo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'month'
          }), valueCallback);
        // Jan, Feb, ..., Dec
        case 'MMM':
          return match.month(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // J, F, ..., D
        case 'MMMMM':
          return match.month(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // January, February, ..., December
        case 'MMMM':
        default:
          return match.month(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.month(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 11;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth(value, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return MonthParser;
}(Parser);

var StandAloneMonthParser = /*#__PURE__*/function (_Parser) {
  _inherits(StandAloneMonthParser, _Parser);
  var _super = _createSuper(StandAloneMonthParser);
  function StandAloneMonthParser() {
    var _this;
    _classCallCheck(this, StandAloneMonthParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 110);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'M', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(StandAloneMonthParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(value) {
        return value - 1;
      };
      switch (token) {
        // 1, 2, ..., 12
        case 'L':
          return mapValue(parseNumericPattern(numericPatterns.month, dateString), valueCallback);
        // 01, 02, ..., 12
        case 'LL':
          return mapValue(parseNDigits(2, dateString), valueCallback);
        // 1st, 2nd, ..., 12th
        case 'Lo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'month'
          }), valueCallback);
        // Jan, Feb, ..., Dec
        case 'LLL':
          return match.month(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // J, F, ..., D
        case 'LLLLL':
          return match.month(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // January, February, ..., December
        case 'LLLL':
        default:
          return match.month(dateString, {
            width: 'wide',
            context: 'standalone'
          }) || match.month(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 11;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth(value, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return StandAloneMonthParser;
}(Parser);

function setUTCWeek(dirtyDate, dirtyWeek, options) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var week = toInteger(dirtyWeek);
  var diff = getUTCWeek(date, options) - week;
  date.setUTCDate(date.getUTCDate() - diff * 7);
  return date;
}

var LocalWeekParser = /*#__PURE__*/function (_Parser) {
  _inherits(LocalWeekParser, _Parser);
  var _super = _createSuper(LocalWeekParser);
  function LocalWeekParser() {
    var _this;
    _classCallCheck(this, LocalWeekParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 100);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'i', 't', 'T']);
    return _this;
  }
  _createClass(LocalWeekParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'w':
          return parseNumericPattern(numericPatterns.week, dateString);
        case 'wo':
          return match.ordinalNumber(dateString, {
            unit: 'week'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 53;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      return startOfUTCWeek(setUTCWeek(date, value, options), options);
    }
  }]);
  return LocalWeekParser;
}(Parser);

function setUTCISOWeek(dirtyDate, dirtyISOWeek) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var isoWeek = toInteger(dirtyISOWeek);
  var diff = getUTCISOWeek(date) - isoWeek;
  date.setUTCDate(date.getUTCDate() - diff * 7);
  return date;
}

var ISOWeekParser = /*#__PURE__*/function (_Parser) {
  _inherits(ISOWeekParser, _Parser);
  var _super = _createSuper(ISOWeekParser);
  function ISOWeekParser() {
    var _this;
    _classCallCheck(this, ISOWeekParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 100);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['y', 'Y', 'u', 'q', 'Q', 'M', 'L', 'w', 'd', 'D', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(ISOWeekParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'I':
          return parseNumericPattern(numericPatterns.week, dateString);
        case 'Io':
          return match.ordinalNumber(dateString, {
            unit: 'week'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 53;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      return startOfUTCISOWeek(setUTCISOWeek(date, value));
    }
  }]);
  return ISOWeekParser;
}(Parser);

var DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
var DAYS_IN_MONTH_LEAP_YEAR = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Day of the month
var DateParser = /*#__PURE__*/function (_Parser) {
  _inherits(DateParser, _Parser);
  var _super = _createSuper(DateParser);
  function DateParser() {
    var _this;
    _classCallCheck(this, DateParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 90);
    _defineProperty(_assertThisInitialized(_this), "subPriority", 1);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(DateParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'd':
          return parseNumericPattern(numericPatterns.date, dateString);
        case 'do':
          return match.ordinalNumber(dateString, {
            unit: 'date'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(date, value) {
      var year = date.getUTCFullYear();
      var isLeapYear = isLeapYearIndex(year);
      var month = date.getUTCMonth();
      if (isLeapYear) {
        return value >= 1 && value <= DAYS_IN_MONTH_LEAP_YEAR[month];
      } else {
        return value >= 1 && value <= DAYS_IN_MONTH[month];
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCDate(value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return DateParser;
}(Parser);

var DayOfYearParser = /*#__PURE__*/function (_Parser) {
  _inherits(DayOfYearParser, _Parser);
  var _super = _createSuper(DayOfYearParser);
  function DayOfYearParser() {
    var _this;
    _classCallCheck(this, DayOfYearParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 90);
    _defineProperty(_assertThisInitialized(_this), "subpriority", 1);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'M', 'L', 'w', 'I', 'd', 'E', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(DayOfYearParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'D':
        case 'DD':
          return parseNumericPattern(numericPatterns.dayOfYear, dateString);
        case 'Do':
          return match.ordinalNumber(dateString, {
            unit: 'date'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(date, value) {
      var year = date.getUTCFullYear();
      var isLeapYear = isLeapYearIndex(year);
      if (isLeapYear) {
        return value >= 1 && value <= 366;
      } else {
        return value >= 1 && value <= 365;
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth(0, value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return DayOfYearParser;
}(Parser);

function setUTCDay(dirtyDate, dirtyDay, options) {
  var _ref, _ref2, _ref3, _options$weekStartsOn, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;
  requiredArgs(2, arguments);
  var defaultOptions = getDefaultOptions();
  var weekStartsOn = toInteger((_ref = (_ref2 = (_ref3 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.weekStartsOn) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.weekStartsOn) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.weekStartsOn) !== null && _ref !== void 0 ? _ref : 0);

  // Test if weekStartsOn is between 0 and 6 _and_ is not NaN
  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }
  var date = toDate(dirtyDate);
  var day = toInteger(dirtyDay);
  var currentDay = date.getUTCDay();
  var remainder = day % 7;
  var dayIndex = (remainder + 7) % 7;
  var diff = (dayIndex < weekStartsOn ? 7 : 0) + day - currentDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

var DayParser = /*#__PURE__*/function (_Parser) {
  _inherits(DayParser, _Parser);
  var _super = _createSuper(DayParser);
  function DayParser() {
    var _this;
    _classCallCheck(this, DayParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 90);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['D', 'i', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(DayParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // Tue
        case 'E':
        case 'EE':
        case 'EEE':
          return match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // T
        case 'EEEEE':
          return match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tu
        case 'EEEEEE':
          return match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tuesday
        case 'EEEE':
        default:
          return match.day(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 6;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return DayParser;
}(Parser);

var LocalDayParser = /*#__PURE__*/function (_Parser) {
  _inherits(LocalDayParser, _Parser);
  var _super = _createSuper(LocalDayParser);
  function LocalDayParser() {
    var _this;
    _classCallCheck(this, LocalDayParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 90);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'E', 'i', 'c', 't', 'T']);
    return _this;
  }
  _createClass(LocalDayParser, [{
    key: "parse",
    value: function parse(dateString, token, match, options) {
      var valueCallback = function valueCallback(value) {
        var wholeWeekDays = Math.floor((value - 1) / 7) * 7;
        return (value + options.weekStartsOn + 6) % 7 + wholeWeekDays;
      };
      switch (token) {
        // 3
        case 'e':
        case 'ee':
          // 03
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
        // 3rd
        case 'eo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'day'
          }), valueCallback);
        // Tue
        case 'eee':
          return match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // T
        case 'eeeee':
          return match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tu
        case 'eeeeee':
          return match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tuesday
        case 'eeee':
        default:
          return match.day(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 6;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return LocalDayParser;
}(Parser);

var StandAloneLocalDayParser = /*#__PURE__*/function (_Parser) {
  _inherits(StandAloneLocalDayParser, _Parser);
  var _super = _createSuper(StandAloneLocalDayParser);
  function StandAloneLocalDayParser() {
    var _this;
    _classCallCheck(this, StandAloneLocalDayParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 90);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'E', 'i', 'e', 't', 'T']);
    return _this;
  }
  _createClass(StandAloneLocalDayParser, [{
    key: "parse",
    value: function parse(dateString, token, match, options) {
      var valueCallback = function valueCallback(value) {
        var wholeWeekDays = Math.floor((value - 1) / 7) * 7;
        return (value + options.weekStartsOn + 6) % 7 + wholeWeekDays;
      };
      switch (token) {
        // 3
        case 'c':
        case 'cc':
          // 03
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
        // 3rd
        case 'co':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'day'
          }), valueCallback);
        // Tue
        case 'ccc':
          return match.day(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'short',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // T
        case 'ccccc':
          return match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // Tu
        case 'cccccc':
          return match.day(dateString, {
            width: 'short',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // Tuesday
        case 'cccc':
        default:
          return match.day(dateString, {
            width: 'wide',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'short',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 6;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return StandAloneLocalDayParser;
}(Parser);

function setUTCISODay(dirtyDate, dirtyDay) {
  requiredArgs(2, arguments);
  var day = toInteger(dirtyDay);
  if (day % 7 === 0) {
    day = day - 7;
  }
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var currentDay = date.getUTCDay();
  var remainder = day % 7;
  var dayIndex = (remainder + 7) % 7;
  var diff = (dayIndex < weekStartsOn ? 7 : 0) + day - currentDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

var ISODayParser = /*#__PURE__*/function (_Parser) {
  _inherits(ISODayParser, _Parser);
  var _super = _createSuper(ISODayParser);
  function ISODayParser() {
    var _this;
    _classCallCheck(this, ISODayParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 90);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['y', 'Y', 'u', 'q', 'Q', 'M', 'L', 'w', 'd', 'D', 'E', 'e', 'c', 't', 'T']);
    return _this;
  }
  _createClass(ISODayParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(value) {
        if (value === 0) {
          return 7;
        }
        return value;
      };
      switch (token) {
        // 2
        case 'i':
        case 'ii':
          // 02
          return parseNDigits(token.length, dateString);
        // 2nd
        case 'io':
          return match.ordinalNumber(dateString, {
            unit: 'day'
          });
        // Tue
        case 'iii':
          return mapValue(match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
        // T
        case 'iiiii':
          return mapValue(match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
        // Tu
        case 'iiiiii':
          return mapValue(match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
        // Tuesday
        case 'iiii':
        default:
          return mapValue(match.day(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 7;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date = setUTCISODay(date, value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);
  return ISODayParser;
}(Parser);

var AMPMParser = /*#__PURE__*/function (_Parser) {
  _inherits(AMPMParser, _Parser);
  var _super = _createSuper(AMPMParser);
  function AMPMParser() {
    var _this;
    _classCallCheck(this, AMPMParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 80);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['b', 'B', 'H', 'k', 't', 'T']);
    return _this;
  }
  _createClass(AMPMParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'a':
        case 'aa':
        case 'aaa':
          return match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        case 'aaaaa':
          return match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        case 'aaaa':
        default:
          return match.dayPeriod(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    }
  }]);
  return AMPMParser;
}(Parser);

var AMPMMidnightParser = /*#__PURE__*/function (_Parser) {
  _inherits(AMPMMidnightParser, _Parser);
  var _super = _createSuper(AMPMMidnightParser);
  function AMPMMidnightParser() {
    var _this;
    _classCallCheck(this, AMPMMidnightParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 80);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['a', 'B', 'H', 'k', 't', 'T']);
    return _this;
  }
  _createClass(AMPMMidnightParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'b':
        case 'bb':
        case 'bbb':
          return match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        case 'bbbbb':
          return match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        case 'bbbb':
        default:
          return match.dayPeriod(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    }
  }]);
  return AMPMMidnightParser;
}(Parser);

var DayPeriodParser = /*#__PURE__*/function (_Parser) {
  _inherits(DayPeriodParser, _Parser);
  var _super = _createSuper(DayPeriodParser);
  function DayPeriodParser() {
    var _this;
    _classCallCheck(this, DayPeriodParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 80);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['a', 'b', 't', 'T']);
    return _this;
  }
  _createClass(DayPeriodParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'B':
        case 'BB':
        case 'BBB':
          return match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        case 'BBBBB':
          return match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        case 'BBBB':
        default:
          return match.dayPeriod(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    }
  }]);
  return DayPeriodParser;
}(Parser);

var Hour1to12Parser = /*#__PURE__*/function (_Parser) {
  _inherits(Hour1to12Parser, _Parser);
  var _super = _createSuper(Hour1to12Parser);
  function Hour1to12Parser() {
    var _this;
    _classCallCheck(this, Hour1to12Parser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 70);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['H', 'K', 'k', 't', 'T']);
    return _this;
  }
  _createClass(Hour1to12Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'h':
          return parseNumericPattern(numericPatterns.hour12h, dateString);
        case 'ho':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 12;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      var isPM = date.getUTCHours() >= 12;
      if (isPM && value < 12) {
        date.setUTCHours(value + 12, 0, 0, 0);
      } else if (!isPM && value === 12) {
        date.setUTCHours(0, 0, 0, 0);
      } else {
        date.setUTCHours(value, 0, 0, 0);
      }
      return date;
    }
  }]);
  return Hour1to12Parser;
}(Parser);

var Hour0to23Parser = /*#__PURE__*/function (_Parser) {
  _inherits(Hour0to23Parser, _Parser);
  var _super = _createSuper(Hour0to23Parser);
  function Hour0to23Parser() {
    var _this;
    _classCallCheck(this, Hour0to23Parser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 70);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['a', 'b', 'h', 'K', 'k', 't', 'T']);
    return _this;
  }
  _createClass(Hour0to23Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'H':
          return parseNumericPattern(numericPatterns.hour23h, dateString);
        case 'Ho':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 23;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(value, 0, 0, 0);
      return date;
    }
  }]);
  return Hour0to23Parser;
}(Parser);

var Hour0To11Parser = /*#__PURE__*/function (_Parser) {
  _inherits(Hour0To11Parser, _Parser);
  var _super = _createSuper(Hour0To11Parser);
  function Hour0To11Parser() {
    var _this;
    _classCallCheck(this, Hour0To11Parser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 70);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['h', 'H', 'k', 't', 'T']);
    return _this;
  }
  _createClass(Hour0To11Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'K':
          return parseNumericPattern(numericPatterns.hour11h, dateString);
        case 'Ko':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 11;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      var isPM = date.getUTCHours() >= 12;
      if (isPM && value < 12) {
        date.setUTCHours(value + 12, 0, 0, 0);
      } else {
        date.setUTCHours(value, 0, 0, 0);
      }
      return date;
    }
  }]);
  return Hour0To11Parser;
}(Parser);

var Hour1To24Parser = /*#__PURE__*/function (_Parser) {
  _inherits(Hour1To24Parser, _Parser);
  var _super = _createSuper(Hour1To24Parser);
  function Hour1To24Parser() {
    var _this;
    _classCallCheck(this, Hour1To24Parser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 70);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['a', 'b', 'h', 'H', 'K', 't', 'T']);
    return _this;
  }
  _createClass(Hour1To24Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'k':
          return parseNumericPattern(numericPatterns.hour24h, dateString);
        case 'ko':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 24;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      var hours = value <= 24 ? value % 24 : value;
      date.setUTCHours(hours, 0, 0, 0);
      return date;
    }
  }]);
  return Hour1To24Parser;
}(Parser);

var MinuteParser = /*#__PURE__*/function (_Parser) {
  _inherits(MinuteParser, _Parser);
  var _super = _createSuper(MinuteParser);
  function MinuteParser() {
    var _this;
    _classCallCheck(this, MinuteParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 60);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['t', 'T']);
    return _this;
  }
  _createClass(MinuteParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'm':
          return parseNumericPattern(numericPatterns.minute, dateString);
        case 'mo':
          return match.ordinalNumber(dateString, {
            unit: 'minute'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 59;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMinutes(value, 0, 0);
      return date;
    }
  }]);
  return MinuteParser;
}(Parser);

var SecondParser = /*#__PURE__*/function (_Parser) {
  _inherits(SecondParser, _Parser);
  var _super = _createSuper(SecondParser);
  function SecondParser() {
    var _this;
    _classCallCheck(this, SecondParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 50);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['t', 'T']);
    return _this;
  }
  _createClass(SecondParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 's':
          return parseNumericPattern(numericPatterns.second, dateString);
        case 'so':
          return match.ordinalNumber(dateString, {
            unit: 'second'
          });
        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 59;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCSeconds(value, 0);
      return date;
    }
  }]);
  return SecondParser;
}(Parser);

var FractionOfSecondParser = /*#__PURE__*/function (_Parser) {
  _inherits(FractionOfSecondParser, _Parser);
  var _super = _createSuper(FractionOfSecondParser);
  function FractionOfSecondParser() {
    var _this;
    _classCallCheck(this, FractionOfSecondParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 30);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['t', 'T']);
    return _this;
  }
  _createClass(FractionOfSecondParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      var valueCallback = function valueCallback(value) {
        return Math.floor(value * Math.pow(10, -token.length + 3));
      };
      return mapValue(parseNDigits(token.length, dateString), valueCallback);
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMilliseconds(value);
      return date;
    }
  }]);
  return FractionOfSecondParser;
}(Parser);

var ISOTimezoneWithZParser = /*#__PURE__*/function (_Parser) {
  _inherits(ISOTimezoneWithZParser, _Parser);
  var _super = _createSuper(ISOTimezoneWithZParser);
  function ISOTimezoneWithZParser() {
    var _this;
    _classCallCheck(this, ISOTimezoneWithZParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 10);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['t', 'T', 'x']);
    return _this;
  }
  _createClass(ISOTimezoneWithZParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      switch (token) {
        case 'X':
          return parseTimezonePattern(timezonePatterns.basicOptionalMinutes, dateString);
        case 'XX':
          return parseTimezonePattern(timezonePatterns.basic, dateString);
        case 'XXXX':
          return parseTimezonePattern(timezonePatterns.basicOptionalSeconds, dateString);
        case 'XXXXX':
          return parseTimezonePattern(timezonePatterns.extendedOptionalSeconds, dateString);
        case 'XXX':
        default:
          return parseTimezonePattern(timezonePatterns.extended, dateString);
      }
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      if (flags.timestampIsSet) {
        return date;
      }
      return new Date(date.getTime() - value);
    }
  }]);
  return ISOTimezoneWithZParser;
}(Parser);

var ISOTimezoneParser = /*#__PURE__*/function (_Parser) {
  _inherits(ISOTimezoneParser, _Parser);
  var _super = _createSuper(ISOTimezoneParser);
  function ISOTimezoneParser() {
    var _this;
    _classCallCheck(this, ISOTimezoneParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 10);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", ['t', 'T', 'X']);
    return _this;
  }
  _createClass(ISOTimezoneParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      switch (token) {
        case 'x':
          return parseTimezonePattern(timezonePatterns.basicOptionalMinutes, dateString);
        case 'xx':
          return parseTimezonePattern(timezonePatterns.basic, dateString);
        case 'xxxx':
          return parseTimezonePattern(timezonePatterns.basicOptionalSeconds, dateString);
        case 'xxxxx':
          return parseTimezonePattern(timezonePatterns.extendedOptionalSeconds, dateString);
        case 'xxx':
        default:
          return parseTimezonePattern(timezonePatterns.extended, dateString);
      }
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      if (flags.timestampIsSet) {
        return date;
      }
      return new Date(date.getTime() - value);
    }
  }]);
  return ISOTimezoneParser;
}(Parser);

var TimestampSecondsParser = /*#__PURE__*/function (_Parser) {
  _inherits(TimestampSecondsParser, _Parser);
  var _super = _createSuper(TimestampSecondsParser);
  function TimestampSecondsParser() {
    var _this;
    _classCallCheck(this, TimestampSecondsParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 40);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", '*');
    return _this;
  }
  _createClass(TimestampSecondsParser, [{
    key: "parse",
    value: function parse(dateString) {
      return parseAnyDigitsSigned(dateString);
    }
  }, {
    key: "set",
    value: function set(_date, _flags, value) {
      return [new Date(value * 1000), {
        timestampIsSet: true
      }];
    }
  }]);
  return TimestampSecondsParser;
}(Parser);

var TimestampMillisecondsParser = /*#__PURE__*/function (_Parser) {
  _inherits(TimestampMillisecondsParser, _Parser);
  var _super = _createSuper(TimestampMillisecondsParser);
  function TimestampMillisecondsParser() {
    var _this;
    _classCallCheck(this, TimestampMillisecondsParser);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _super.call.apply(_super, [this].concat(args));
    _defineProperty(_assertThisInitialized(_this), "priority", 20);
    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", '*');
    return _this;
  }
  _createClass(TimestampMillisecondsParser, [{
    key: "parse",
    value: function parse(dateString) {
      return parseAnyDigitsSigned(dateString);
    }
  }, {
    key: "set",
    value: function set(_date, _flags, value) {
      return [new Date(value), {
        timestampIsSet: true
      }];
    }
  }]);
  return TimestampMillisecondsParser;
}(Parser);

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O* | Timezone (GMT)                 |
 * |  p  |                                |  P  |                                |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z* | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `parse` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 */
var parsers = {
  G: new EraParser(),
  y: new YearParser(),
  Y: new LocalWeekYearParser(),
  R: new ISOWeekYearParser(),
  u: new ExtendedYearParser(),
  Q: new QuarterParser(),
  q: new StandAloneQuarterParser(),
  M: new MonthParser(),
  L: new StandAloneMonthParser(),
  w: new LocalWeekParser(),
  I: new ISOWeekParser(),
  d: new DateParser(),
  D: new DayOfYearParser(),
  E: new DayParser(),
  e: new LocalDayParser(),
  c: new StandAloneLocalDayParser(),
  i: new ISODayParser(),
  a: new AMPMParser(),
  b: new AMPMMidnightParser(),
  B: new DayPeriodParser(),
  h: new Hour1to12Parser(),
  H: new Hour0to23Parser(),
  K: new Hour0To11Parser(),
  k: new Hour1To24Parser(),
  m: new MinuteParser(),
  s: new SecondParser(),
  S: new FractionOfSecondParser(),
  X: new ISOTimezoneWithZParser(),
  x: new ISOTimezoneParser(),
  t: new TimestampSecondsParser(),
  T: new TimestampMillisecondsParser()
};

var formatDistanceLocale$1 = {
  lessThanXSeconds: {
    one: 'menos de un segundo',
    other: 'menos de {{count}} segundos'
  },
  xSeconds: {
    one: '1 segundo',
    other: '{{count}} segundos'
  },
  halfAMinute: 'medio minuto',
  lessThanXMinutes: {
    one: 'menos de un minuto',
    other: 'menos de {{count}} minutos'
  },
  xMinutes: {
    one: '1 minuto',
    other: '{{count}} minutos'
  },
  aboutXHours: {
    one: 'alrededor de 1 hora',
    other: 'alrededor de {{count}} horas'
  },
  xHours: {
    one: '1 hora',
    other: '{{count}} horas'
  },
  xDays: {
    one: '1 día',
    other: '{{count}} días'
  },
  aboutXWeeks: {
    one: 'alrededor de 1 semana',
    other: 'alrededor de {{count}} semanas'
  },
  xWeeks: {
    one: '1 semana',
    other: '{{count}} semanas'
  },
  aboutXMonths: {
    one: 'alrededor de 1 mes',
    other: 'alrededor de {{count}} meses'
  },
  xMonths: {
    one: '1 mes',
    other: '{{count}} meses'
  },
  aboutXYears: {
    one: 'alrededor de 1 año',
    other: 'alrededor de {{count}} años'
  },
  xYears: {
    one: '1 año',
    other: '{{count}} años'
  },
  overXYears: {
    one: 'más de 1 año',
    other: 'más de {{count}} años'
  },
  almostXYears: {
    one: 'casi 1 año',
    other: 'casi {{count}} años'
  }
};
var formatDistance$1 = function formatDistance(token, count, options) {
  var result;
  var tokenValue = formatDistanceLocale$1[token];
  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', count.toString());
  }
  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'en ' + result;
    } else {
      return 'hace ' + result;
    }
  }
  return result;
};

var dateFormats$1 = {
  full: "EEEE, d 'de' MMMM 'de' y",
  "long": "d 'de' MMMM 'de' y",
  medium: 'd MMM y',
  "short": 'dd/MM/y'
};
var timeFormats$1 = {
  full: 'HH:mm:ss zzzz',
  "long": 'HH:mm:ss z',
  medium: 'HH:mm:ss',
  "short": 'HH:mm'
};
var dateTimeFormats$1 = {
  full: "{{date}} 'a las' {{time}}",
  "long": "{{date}} 'a las' {{time}}",
  medium: '{{date}}, {{time}}',
  "short": '{{date}}, {{time}}'
};
var formatLong$1 = {
  date: buildFormatLongFn({
    formats: dateFormats$1,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats$1,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats$1,
    defaultWidth: 'full'
  })
};

var formatRelativeLocale$1 = {
  lastWeek: "'el' eeee 'pasado a la' p",
  yesterday: "'ayer a la' p",
  today: "'hoy a la' p",
  tomorrow: "'mañana a la' p",
  nextWeek: "eeee 'a la' p",
  other: 'P'
};
var formatRelativeLocalePlural = {
  lastWeek: "'el' eeee 'pasado a las' p",
  yesterday: "'ayer a las' p",
  today: "'hoy a las' p",
  tomorrow: "'mañana a las' p",
  nextWeek: "eeee 'a las' p",
  other: 'P'
};
var formatRelative$1 = function formatRelative(token, date, _baseDate, _options) {
  if (date.getUTCHours() !== 1) {
    return formatRelativeLocalePlural[token];
  } else {
    return formatRelativeLocale$1[token];
  }
};

var eraValues$1 = {
  narrow: ['AC', 'DC'],
  abbreviated: ['AC', 'DC'],
  wide: ['antes de cristo', 'después de cristo']
};
var quarterValues$1 = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['T1', 'T2', 'T3', 'T4'],
  wide: ['1º trimestre', '2º trimestre', '3º trimestre', '4º trimestre']
};
var monthValues$1 = {
  narrow: ['e', 'f', 'm', 'a', 'm', 'j', 'j', 'a', 's', 'o', 'n', 'd'],
  abbreviated: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  wide: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
};
var dayValues$1 = {
  narrow: ['d', 'l', 'm', 'm', 'j', 'v', 's'],
  "short": ['do', 'lu', 'ma', 'mi', 'ju', 'vi', 'sá'],
  abbreviated: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  wide: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
};
var dayPeriodValues$1 = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mn',
    noon: 'md',
    morning: 'mañana',
    afternoon: 'tarde',
    evening: 'tarde',
    night: 'noche'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'mañana',
    afternoon: 'tarde',
    evening: 'tarde',
    night: 'noche'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'mañana',
    afternoon: 'tarde',
    evening: 'tarde',
    night: 'noche'
  }
};
var formattingDayPeriodValues$1 = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mn',
    noon: 'md',
    morning: 'de la mañana',
    afternoon: 'de la tarde',
    evening: 'de la tarde',
    night: 'de la noche'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'de la mañana',
    afternoon: 'de la tarde',
    evening: 'de la tarde',
    night: 'de la noche'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'medianoche',
    noon: 'mediodia',
    morning: 'de la mañana',
    afternoon: 'de la tarde',
    evening: 'de la tarde',
    night: 'de la noche'
  }
};
var ordinalNumber$1 = function ordinalNumber(dirtyNumber, _options) {
  var number = Number(dirtyNumber);
  return number + 'º';
};
var localize$1 = {
  ordinalNumber: ordinalNumber$1,
  era: buildLocalizeFn({
    values: eraValues$1,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues$1,
    defaultWidth: 'wide',
    argumentCallback: function argumentCallback(quarter) {
      return Number(quarter) - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues$1,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues$1,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues$1,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues$1,
    defaultFormattingWidth: 'wide'
  })
};

var matchOrdinalNumberPattern$1 = /^(\d+)(º)?/i;
var parseOrdinalNumberPattern$1 = /\d+/i;
var matchEraPatterns$1 = {
  narrow: /^(ac|dc|a|d)/i,
  abbreviated: /^(a\.?\s?c\.?|a\.?\s?e\.?\s?c\.?|d\.?\s?c\.?|e\.?\s?c\.?)/i,
  wide: /^(antes de cristo|antes de la era com[uú]n|despu[eé]s de cristo|era com[uú]n)/i
};
var parseEraPatterns$1 = {
  any: [/^ac/i, /^dc/i],
  wide: [/^(antes de cristo|antes de la era com[uú]n)/i, /^(despu[eé]s de cristo|era com[uú]n)/i]
};
var matchQuarterPatterns$1 = {
  narrow: /^[1234]/i,
  abbreviated: /^T[1234]/i,
  wide: /^[1234](º)? trimestre/i
};
var parseQuarterPatterns$1 = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns$1 = {
  narrow: /^[efmajsond]/i,
  abbreviated: /^(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)/i,
  wide: /^(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)/i
};
var parseMonthPatterns$1 = {
  narrow: [/^e/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^en/i, /^feb/i, /^mar/i, /^abr/i, /^may/i, /^jun/i, /^jul/i, /^ago/i, /^sep/i, /^oct/i, /^nov/i, /^dic/i]
};
var matchDayPatterns$1 = {
  narrow: /^[dlmjvs]/i,
  "short": /^(do|lu|ma|mi|ju|vi|s[áa])/i,
  abbreviated: /^(dom|lun|mar|mi[ée]|jue|vie|s[áa]b)/i,
  wide: /^(domingo|lunes|martes|mi[ée]rcoles|jueves|viernes|s[áa]bado)/i
};
var parseDayPatterns$1 = {
  narrow: [/^d/i, /^l/i, /^m/i, /^m/i, /^j/i, /^v/i, /^s/i],
  any: [/^do/i, /^lu/i, /^ma/i, /^mi/i, /^ju/i, /^vi/i, /^sa/i]
};
var matchDayPeriodPatterns$1 = {
  narrow: /^(a|p|mn|md|(de la|a las) (mañana|tarde|noche))/i,
  any: /^([ap]\.?\s?m\.?|medianoche|mediodia|(de la|a las) (mañana|tarde|noche))/i
};
var parseDayPeriodPatterns$1 = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mn/i,
    noon: /^md/i,
    morning: /mañana/i,
    afternoon: /tarde/i,
    evening: /tarde/i,
    night: /noche/i
  }
};
var match$1 = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern$1,
    parsePattern: parseOrdinalNumberPattern$1,
    valueCallback: function valueCallback(value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns$1,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns$1,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns$1,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns$1,
    defaultParseWidth: 'any',
    valueCallback: function valueCallback(index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns$1,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns$1,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns$1,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns$1,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns$1,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns$1,
    defaultParseWidth: 'any'
  })
};

/**
 * @type {Locale}
 * @category Locales
 * @summary Spanish locale.
 * @language Spanish
 * @iso-639-2 spa
 * @author Juan Angosto [@juanangosto]{@link https://github.com/juanangosto}
 * @author Guillermo Grau [@guigrpa]{@link https://github.com/guigrpa}
 * @author Fernando Agüero [@fjaguero]{@link https://github.com/fjaguero}
 * @author Gastón Haro [@harogaston]{@link https://github.com/harogaston}
 * @author Yago Carballo [@YagoCarballo]{@link https://github.com/YagoCarballo}
 */
var locale$1 = {
  code: 'es',
  formatDistance: formatDistance$1,
  formatLong: formatLong$1,
  formatRelative: formatRelative$1,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 1 /* Monday */,
    firstWeekContainsDate: 1
  }
};

var redColor = '#db2e00';
var width = '229px';
var Label = styled.label.withConfig({
  displayName: "AmplifyDateInputStyles__Label",
  componentId: "sc-z3zwb5-0"
})(["margin-bottom:0.15rem !important;.input-label-title{font-weight:bold;}"]);
var WrapperDiv = styled.div.withConfig({
  displayName: "AmplifyDateInputStyles__WrapperDiv",
  componentId: "sc-z3zwb5-1"
})(["position:relative;z-index:3;.input-label-validation{color:", ";}&.calendarIsOpen{z-index:4;}"], redColor);
var InputDiv = styled.div.withConfig({
  displayName: "AmplifyDateInputStyles__InputDiv",
  componentId: "sc-z3zwb5-2"
})(["display:flex;align-items:center;.formatted-date-input{width:", ";&.invalid:focus{outline:3px solid ", ";}}:after{content:'", "';left:", "px;line-height:35px;opacity:0.6;pointer-events:none;position:absolute;}.open-date-panel-btn{position:relative;left:-32px;padding:0;.icon-v2-calendar{color:#3278c8;margin:0;}}.shadow-date-input{overflow:auto;position:fixed;visibility:hidden;width:auto;}"], width, redColor, function (_ref) {
  var placeholder = _ref.placeholder;
  return placeholder;
}, function (_ref2) {
  var placeholderLeft = _ref2.placeholderLeft;
  return placeholderLeft;
});
var DatePanelDiv = styled.div.withConfig({
  displayName: "AmplifyDateInputStyles__DatePanelDiv",
  componentId: "sc-z3zwb5-3"
})(["position:absolute;top:", ";right:", ";bottom:", ";&.hidden{display:none;}&:after,&:before{top:", ";left:", ";border:solid transparent;content:' ';height:0;width:0;position:absolute;pointer-events:none;transform:", ";}&:after{border-color:rgba(255,255,255,0);border-top-color:#fff;border-width:10px;margin-right:-10px;}&:before{border-color:rgba(211,211,211,0);border-top-color:rgba(0,0,0,0.2);border-width:", ";margin-right:-12px;}.close-date-panel-btn{background-color:transparent;color:#3b3b3b;position:absolute;width:48px;height:48px;right:0;top:0;border:0;opacity:0.4;cursor:pointer;&:hover{opacity:1;}}"], function (props) {
  return props.styledComponentsProps.top;
}, function (props) {
  return props.styledComponentsProps.right;
}, function (props) {
  return props.styledComponentsProps.bottom || 'calc(100% + 8px)';
}, function (props) {
  return props.styledComponentsProps.pseudoElTop || '100%';
}, function (props) {
  return props.styledComponentsProps.pseudoElLeft || '85%';
}, function (props) {
  return props.styledComponentsProps.transform;
}, function (props) {
  return props.styledComponentsProps.pseudoElBorderWidth || '12px';
});

var endsWithDateDelimiterRegex = /(\/|\.|-|\s)$/g;
var endsWithDelimiterRegex = /(\/|\.|-|\s|:)$/g;
var dateDelimiterRegex = /\/|\.|-|\s/g;
var alphaRegex = /[a-zA-Z]/g;
var numericRegex = /[0-9]/g;
var delimiterRegex = /\/|:|-|\s|\./g;

// LOCALE DATE SECTION

var LOCALE_CONSTANTS = {
  EN: 'en',
  ES: 'es'
};
var esDateObject = {
  correctedSegmentsFromInput: function correctedSegmentsFromInput(day, month, year, twelveHour, minute, period, inputValue) {
    return getEsSegmentsFromInput(day, month, year, twelveHour, minute, period, inputValue);
  },
  getFormatInputValue: function getFormatInputValue(day, month, year) {
    return esFormatInputValueDate(day, month, year);
  },
  lastSegment: 'day',
  initialCursorPositions: function initialCursorPositions() {
    return getESInitialCursorPositions();
  },
  segmentMap: function segmentMap() {
    return getESsegmentMap();
  },
  getSegments: function getSegments(month, day, year, twelveHour, minute, period, inputValue) {
    return getESSegments(month, day, year, twelveHour, minute, period);
  }
};
var enDateObject = {
  correctedSegmentsFromInput: function correctedSegmentsFromInput(day, month, year, twelveHour, minute, period, inputValue) {
    return getEnSegmentsFromInput(day, month, year, twelveHour, minute, period, inputValue);
  },
  getFormatInputValue: function getFormatInputValue(day, month, year) {
    return enFormatInputValueDate(day, month, year);
  },
  lastSegment: 'month',
  initialCursorPositions: function initialCursorPositions() {
    return getENInitialCursorPositions();
  },
  segmentMap: function segmentMap() {
    return getENsegmentMap();
  },
  getSegments: function getSegments(month, day, year, twelveHour, minute, period, inputValue) {
    return getENSegments(month, day, year, twelveHour, minute, period);
  }
};
var localeFormatMap = new Map();
localeFormatMap.set(LOCALE_CONSTANTS.ES, esDateObject);
localeFormatMap.set(LOCALE_CONSTANTS.EN, enDateObject);
var removeLeadingZero = function removeLeadingZero(value) {
  var shouldRemoveLeadingZero = (value === null || value === void 0 ? void 0 : value.length) === 3 && (value === null || value === void 0 ? void 0 : value.startsWith('0'));
  return shouldRemoveLeadingZero ? value === null || value === void 0 ? void 0 : value.replace(/^0/, '') : value;
};
var formatTwelveHour = function formatTwelveHour(twelveHour, minute, period) {
  var inputValue = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  if (twelveHour) {
    if (inputValue !== null && inputValue !== void 0 && inputValue.match(endsWithDelimiterRegex)) {
      return padTime(twelveHour);
    } else if (!minute && !period) {
      return twelveHour;
    } else if (minute) {
      return removeLeadingZero(twelveHour);
    }
  }
};
var handleDateDelimiter = function handleDateDelimiter(value) {
  var fixedValue = value.replace(endsWithDateDelimiterRegex, '');
  return fixedValue ? removeLeadingZero(padTime(fixedValue)) : '';
};
var handleMonthOrDay = function handleMonthOrDay(value) {
  return value.match(endsWithDateDelimiterRegex) ? handleDateDelimiter(value) : removeLeadingZero(value);
};
var getESsegmentMap = function getESsegmentMap() {
  return {
    0: 'day',
    1: 'month',
    2: 'year',
    3: 'twelveHour',
    4: 'minute',
    5: 'period'
  };
};
var getENsegmentMap = function getENsegmentMap() {
  return {
    0: 'month',
    1: 'day',
    2: 'year',
    3: 'twelveHour',
    4: 'minute',
    5: 'period'
  };
};
var getESInitialCursorPositions = function getESInitialCursorPositions() {
  return {
    day: {
      selectionStart: null,
      selectionEnd: null
    },
    month: {
      selectionStart: null,
      selectionEnd: null
    },
    year: {
      selectionStart: null,
      selectionEnd: null
    },
    twelveHour: {
      selectionStart: null,
      selectionEnd: null
    },
    minute: {
      selectionStart: null,
      selectionEnd: null
    },
    period: {
      selectionStart: null,
      selectionEnd: null
    }
  };
};
var getENInitialCursorPositions = function getENInitialCursorPositions() {
  return {
    month: {
      selectionStart: null,
      selectionEnd: null
    },
    day: {
      selectionStart: null,
      selectionEnd: null
    },
    year: {
      selectionStart: null,
      selectionEnd: null
    },
    twelveHour: {
      selectionStart: null,
      selectionEnd: null
    },
    minute: {
      selectionStart: null,
      selectionEnd: null
    },
    period: {
      selectionStart: null,
      selectionEnd: null
    }
  };
};
var esFormatInputValueDate = function esFormatInputValueDate(day, month, year) {
  return "".concat(day, "/").concat(month, "/").concat(year);
};
var enFormatInputValueDate = function enFormatInputValueDate(day, month, year) {
  return "".concat(month, "/").concat(day, "/").concat(year);
};
var getEnSegmentsFromInput = function getEnSegmentsFromInput(day, month, year, twelveHour, minute, period, inputValue) {
  return {
    month: handleMonthOrDay(month),
    day: handleMonthOrDay(day),
    year: year ? year.toString().trim() : '',
    twelveHour: formatTwelveHour(twelveHour, minute, period, inputValue),
    minute: removeLeadingZero(minute),
    period: autofillPeriod(period) || ''
  };
};
var getEsSegmentsFromInput = function getEsSegmentsFromInput(day, month, year, twelveHour, minute, period, inputValue) {
  return {
    day: handleMonthOrDay(day),
    month: handleMonthOrDay(month),
    year: year ? year.toString().trim() : '',
    twelveHour: formatTwelveHour(twelveHour, minute, period, inputValue),
    minute: removeLeadingZero(minute),
    period: autofillPeriod(period) || ''
  };
};
var getSegment = function getSegment(value) {
  if (!value) {
    return '';
  }
  return value.endsWith(' ') ? value : value.trim();
};
var getESSegments = function getESSegments(month, day, year, twelveHour, minute, period) {
  return {
    day: getSegment(day),
    month: getSegment(month),
    year: getSegment(year),
    twelveHour: getSegment(twelveHour),
    minute: getSegment(minute),
    period: getSegment(period)
  };
};
var getENSegments = function getENSegments(month, day, year, twelveHour, minute, period) {
  return {
    month: getSegment(month),
    day: getSegment(day),
    year: getSegment(year),
    twelveHour: getSegment(twelveHour),
    minute: getSegment(minute),
    period: getSegment(period)
  };
};

// END LOCALE DATE SECTION

var formatInputValue = function formatInputValue(inputSegments) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var period = inputSegments.period;
  month = month ? padTime(month) : '';
  day = day ? padTime(day) : '';
  twelveHour = twelveHour ? padTime(twelveHour) : '';
  minute = minute || minute === 0 ? padTime(minute) : '';
  year = year || '';
  var date = localeFormatMap.get(language).getFormatInputValue(day, month, year);
  var time = "".concat(twelveHour, ":").concat(minute).concat(period ? ' ' + period : '');
  return "".concat(date, " - ").concat(time);
};
var getCorrectedSegmentsFromInput = function getCorrectedSegmentsFromInput(inputValue) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var inputSegments = mapInputToSegments(inputValue, language);
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var period = inputSegments.period;
  return localeFormatMap.get(language).correctedSegmentsFromInput(day, month, year, twelveHour, minute, period, inputValue);
};
var mapDateToInput = function mapDateToInput(date) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var month = date.getMonth() + 1;
  var day = date.getDate();
  var year = date.getFullYear();
  var hour = date.getHours();
  var minute = date.getMinutes();
  var twelveHour = get12HourFrom24Hour(hour);
  var period = getIsAM(hour) ? 'AM' : 'PM';
  return formatInputValue({
    month: month,
    day: day,
    year: year,
    twelveHour: twelveHour,
    minute: minute,
    period: period
  }, language);
};
var getIsValidDate = function getIsValidDate(date) {
  return date.toString() !== 'Invalid Date';
};
var getIsSegmentsValid = function getIsSegmentsValid(inputSegments) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var isNewInput = getIsNewInput(inputSegments, language);
  if (!isNewInput && year < 1000) {
    return false;
  } else if (month > 12) {
    return false;
  } else if (day > 31) {
    return false;
  } else if (twelveHour > 12) {
    return false;
  } else if (minute > 59) {
    return false;
  }
  return true;
};
var autofillPeriod = function autofillPeriod(period) {
  var am = ['a', 'am'];
  var pm = ['p', 'pm'];
  if (am.includes(period === null || period === void 0 ? void 0 : period.toLowerCase())) {
    return 'AM';
  } else if (pm.includes(period === null || period === void 0 ? void 0 : period.toLowerCase())) {
    return 'PM';
  } else {
    return period;
  }
};
var mapInputSegmentsToDate = function mapInputSegmentsToDate(inputSegments) {
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var period = inputSegments.period;
  var isAM = getIsAM(period);
  var twentyfourHour = get24HourFrom12Hour(twelveHour, isAM);
  return new Date("".concat(month, "/").concat(day, "/").concat(year, " ").concat(twentyfourHour, ":").concat(minute));
};
var mapInputToSegments = function mapInputToSegments(inputValue) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var month, day, year, twelveHour, minute, period;

  // eslint-disable-next-line no-unsafe-optional-chaining
  var _inputValue$split = inputValue === null || inputValue === void 0 ? void 0 : inputValue.split(/\s-\s?/),
    _inputValue$split2 = _slicedToArray(_inputValue$split, 2),
    dateString = _inputValue$split2[0],
    timeString = _inputValue$split2[1];
  if (dateString) {
    if (language === LOCALE_CONSTANTS.EN) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      var _dateString$match = dateString === null || dateString === void 0 ? void 0 : dateString.match(/\d{0,4}(\/|\.|-|\s)?/g);
      var _dateString$match2 = _slicedToArray(_dateString$match, 3);
      month = _dateString$match2[0];
      day = _dateString$match2[1];
      year = _dateString$match2[2];
    } else {
      // eslint-disable-next-line no-unsafe-optional-chaining
      var _dateString$match3 = dateString === null || dateString === void 0 ? void 0 : dateString.match(/\d{0,4}(\/|\.|-|\s)?/g);
      var _dateString$match4 = _slicedToArray(_dateString$match3, 3);
      day = _dateString$match4[0];
      month = _dateString$match4[1];
      year = _dateString$match4[2];
    }
  }
  if (timeString) {
    var _timeString$trim;
    // eslint-disable-next-line no-unsafe-optional-chaining
    var _timeString$trim$spli = timeString === null || timeString === void 0 || (_timeString$trim = timeString.trim()) === null || _timeString$trim === void 0 ? void 0 : _timeString$trim.split(delimiterRegex);
    var _timeString$trim$spli2 = _slicedToArray(_timeString$trim$spli, 3);
    twelveHour = _timeString$trim$spli2[0];
    minute = _timeString$trim$spli2[1];
    period = _timeString$trim$spli2[2];
  }
  return localeFormatMap.get(language).getSegments(month, day, year, twelveHour, minute, period, inputValue);
};
var mapPastedInputToSegments = function mapPastedInputToSegments(inputValue) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var includesDelimiter = inputValue.match(delimiterRegex);
  var isAllDelimiters = inputValue.split('').every(function (character) {
    return character.match(delimiterRegex);
  });
  if (inputValue.length > 2 && !includesDelimiter || isAllDelimiters) {
    return {};
  }
  return getCorrectedSegmentsFromInput(inputValue, language);
};
var getIsFullInputValid = function getIsFullInputValid(inputValue) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var segments = mapPastedInputToSegments(inputValue, language);
  var segmentValues = Object.values(segments);
  return segmentValues.some(function (segmentValue) {
    return segmentValue;
  });
};
var getIsMissingSegment = function getIsMissingSegment(inputSegments) {
  var segments = Object.keys(inputSegments);
  return segments.reduce(function (isMissing, current) {
    if (!isMissing) {
      isMissing = !inputSegments[current];
    }
    return isMissing;
  }, false);
};
var getAutofillInputSegments = function getAutofillInputSegments(inputSegments) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  // we must include the entered delimiter in the autofill segments so that we can decide on if we can autofill when a user enters "1/"
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var autofillMonths = ['2', '3', '4', '5', '6', '7', '8', '9'];
  var autofillDays = ['4', '5', '6', '7', '8', '9'];
  if (language === LOCALE_CONSTANTS.EN) {
    var autofillSegments = {
      month: autofillMonths.includes(month) ? padTime(month) : month || '',
      day: autofillDays.includes(day) ? padTime(day) : day || '',
      year: year || '',
      twelveHour: twelveHour || '',
      minute: minute || ''
    };
    return autofillSegments;
  } else {
    var _autofillSegments = {
      day: autofillDays.includes(day) ? padTime(day) : day || '',
      month: autofillMonths.includes(month) ? padTime(month) : month || '',
      year: year || '',
      twelveHour: twelveHour || '',
      minute: minute || ''
    };
    return _autofillSegments;
  }
};
var getAutofillInputValue = function getAutofillInputValue(autofillSegments) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  if (locale === LOCALE_CONSTANTS.EN) {
    var inputDelimiters = {
      month: '/',
      day: '/',
      year: ' - ',
      twelveHour: ':',
      minute: ' '
    };
    var inputSegments = Object.keys(autofillSegments);
    return inputSegments.reduce(function (autofilledValue, currentSegment) {
      var segmentValue = autofillSegments[currentSegment];
      var shouldAutofill = getShouldAutofillSegment(currentSegment, segmentValue);
      if (segmentValue && shouldAutofill) {
        autofilledValue += segmentValue + inputDelimiters[currentSegment];
      } else {
        autofilledValue += segmentValue;
      }
      return autofilledValue;
    }, '');
  } else {
    var _inputDelimiters = {
      day: '/',
      month: '/',
      year: ' - ',
      twelveHour: ':',
      minute: ' '
    };
    var _inputSegments = Object.keys(autofillSegments);
    return _inputSegments.reduce(function (autofilledValue, currentSegment) {
      var segmentValue = autofillSegments[currentSegment];
      var shouldAutofill = getShouldAutofillSegment(currentSegment, segmentValue);
      if (segmentValue && shouldAutofill) {
        autofilledValue += segmentValue + _inputDelimiters[currentSegment];
      } else {
        autofilledValue += segmentValue;
      }
      return autofilledValue;
    }, '');
  }
};
var getShouldAutofillSegment = function getShouldAutofillSegment(segment, value) {
  if (segment === 'month') {
    return value !== '0' && value !== '1';
  } else if (segment === 'day') {
    return value !== '0' && value !== '1' && value !== '2' && value !== '3';
  } else if (segment === 'year') {
    return (value === null || value === void 0 ? void 0 : value.length) === 4;
  } else if (segment === 'twelveHour') {
    return (value === null || value === void 0 ? void 0 : value.length) === 2;
  } else if (segment === 'minute') {
    return (value === null || value === void 0 ? void 0 : value.length) === 2;
  }
  return true;
};
var getHasTooManyDigits = function getHasTooManyDigits(inputSegments) {
  var _year$replace, _month$replace, _day$replace, _twelveHour$replace, _minute$replace;
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var period = inputSegments.period;
  return (year === null || year === void 0 || (_year$replace = year.replace(endsWithDateDelimiterRegex, '')) === null || _year$replace === void 0 ? void 0 : _year$replace.length) > 4 || (month === null || month === void 0 || (_month$replace = month.replace(endsWithDateDelimiterRegex, '')) === null || _month$replace === void 0 ? void 0 : _month$replace.length) > 2 || (day === null || day === void 0 || (_day$replace = day.replace(endsWithDateDelimiterRegex, '')) === null || _day$replace === void 0 ? void 0 : _day$replace.length) > 2 || (twelveHour === null || twelveHour === void 0 || (_twelveHour$replace = twelveHour.replace(endsWithDateDelimiterRegex, '')) === null || _twelveHour$replace === void 0 ? void 0 : _twelveHour$replace.length) > 2 || (minute === null || minute === void 0 || (_minute$replace = minute.replace(endsWithDateDelimiterRegex, '')) === null || _minute$replace === void 0 ? void 0 : _minute$replace.length) > 2 || (period === null || period === void 0 ? void 0 : period.length) > 2;
};
var getLastSegment = function getLastSegment(inputSegments) {
  var last = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'month';
  var segmentNames = Object.keys(inputSegments).reverse();
  return segmentNames.find(function (segmentName) {
    return inputSegments[segmentName];
  }) || last;
};
var getIsNewInput = function getIsNewInput(inputSegments) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var segmentNames = Object.keys(inputSegments);
  var lastSegment = getLastSegment(inputSegments, localeFormatMap.get(language).lastSegment);
  var lastIndex = segmentNames.indexOf(lastSegment);
  var segmentsBeforeLast = segmentNames.filter(function (segmentName, i) {
    return i < lastIndex;
  });
  var anyPreviousUndefined = segmentsBeforeLast.some(function (segmentName) {
    return !inputSegments[segmentName];
  });
  return lastSegment === 'period' ? inputSegments.period.length < 2 && !anyPreviousUndefined : !anyPreviousUndefined;
};
var getShouldAutofillToNextSegment = function getShouldAutofillToNextSegment(inputSegments) {
  var _year$replace2;
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var period = inputSegments.period;
  var monthDefined = (month === null || month === void 0 ? void 0 : month.length) < 3 && !day && !year && !twelveHour && !minute && !period;
  var monthDayDefined = (month === null || month === void 0 ? void 0 : month.length) === 2 && (day === null || day === void 0 ? void 0 : day.length) < 3 && !year && !twelveHour && !minute && !period;
  var monthDayYearDefined = (month === null || month === void 0 ? void 0 : month.length) === 2 && (day === null || day === void 0 ? void 0 : day.length) === 2 && (year === null || year === void 0 || (_year$replace2 = year.replace(endsWithDateDelimiterRegex, '')) === null || _year$replace2 === void 0 ? void 0 : _year$replace2.length) === 4 && !twelveHour && !minute && !period;
  var monthDayYearHourDefined = (month === null || month === void 0 ? void 0 : month.length) === 2 && (day === null || day === void 0 ? void 0 : day.length) === 2 && (year === null || year === void 0 ? void 0 : year.length) === 4 && (twelveHour === null || twelveHour === void 0 ? void 0 : twelveHour.length) === 2 && !minute && !period;
  var monthDayYearHourMinuteDefined = (month === null || month === void 0 ? void 0 : month.length) === 2 && (day === null || day === void 0 ? void 0 : day.length) === 2 && (year === null || year === void 0 ? void 0 : year.length) === 4 && (twelveHour === null || twelveHour === void 0 ? void 0 : twelveHour.length) === 2 && (minute === null || minute === void 0 ? void 0 : minute.length) === 2 && !period;
  if (!month && !day && !year && !twelveHour && !minute && !period) {
    return false;
  } else if (monthDefined) {
    return true;
  } else if (monthDayDefined) {
    return true;
  } else if (monthDayYearDefined) {
    return true;
  } else if (monthDayYearHourDefined) {
    return true;
  } else if (monthDayYearHourMinuteDefined) {
    return true;
  }
  return false;
};
var getHasInvalidCharacters = function getHasInvalidCharacters(inputSegments) {
  var month = inputSegments.month;
  var day = inputSegments.day;
  var year = inputSegments.year;
  var twelveHour = inputSegments.twelveHour;
  var minute = inputSegments.minute;
  var period = inputSegments.period;
  if (month && month.match(alphaRegex)) {
    return true;
  } else if (day && day.match(alphaRegex)) {
    return true;
  } else if (year && year.match(alphaRegex)) {
    return true;
  } else if (twelveHour && twelveHour.match(alphaRegex)) {
    return true;
  } else if (minute && minute.match(alphaRegex)) {
    return true;
  } else if (period && period.match(numericRegex)) {
    return true;
  } else {
    return false;
  }
};
var getCursorPositionsFromInput = function getCursorPositionsFromInput(inputValue) {
  var locale = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var initialCursorPositions = localeFormatMap.get(locale).initialCursorPositions();
  if (!inputValue) {
    return initialCursorPositions;
  }
  var splitInput = inputValue.split('');
  var segmentMap = localeFormatMap.get(locale).segmentMap();
  var segmentCounter = 0;
  var positions = splitInput.reduce(function (acc, curr, index) {
    var currentSegmentName = segmentMap[segmentCounter];
    if (!currentSegmentName) {
      return acc;
    }
    var currentSegmentCursorPositions = acc[currentSegmentName];
    var isDelimiter = curr.match(delimiterRegex);
    var _ref = [currentSegmentCursorPositions.selectionStart, currentSegmentCursorPositions.selectionEnd],
      start = _ref[0],
      end = _ref[1];
    var setCurrentSelectionStart = function setCurrentSelectionStart() {
      var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : index;
      currentSegmentCursorPositions.selectionStart = value;
    };
    var setCurrentSelectionEnd = function setCurrentSelectionEnd() {
      var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : index;
      currentSegmentCursorPositions.selectionEnd = value;
      segmentCounter++;
    };
    var setLastSelectionStartEnd = function setLastSelectionStartEnd() {
      var nextSegmentCursorPositons = acc[segmentMap[segmentCounter]];
      nextSegmentCursorPositons.selectionStart = splitInput.length;
      nextSegmentCursorPositons.selectionEnd = splitInput.length;
    };
    if (currentSegmentName === 'twelveHour') {
      if (curr === ':' && !start && !end) {
        setCurrentSelectionStart();
        setCurrentSelectionEnd();
      } else if (isDelimiter && start !== null) {
        setCurrentSelectionEnd();
      } else {
        if (!isDelimiter && start === null) {
          setCurrentSelectionStart();
        }
      }
    } else {
      if (isDelimiter && start !== null) {
        setCurrentSelectionEnd();
      } else {
        if ((currentSegmentCursorPositions === null || currentSegmentCursorPositions === void 0 ? void 0 : currentSegmentCursorPositions.selectionStart) === null) {
          if (isDelimiter) {
            setCurrentSelectionStart();
            setCurrentSelectionEnd();
          } else {
            setCurrentSelectionStart();
          }
        }
      }
    }
    if (index === splitInput.length - 1) {
      if (currentSegmentName === 'year') {
        if (!isDelimiter) {
          setCurrentSelectionEnd(splitInput.length);
        }
      } else if (currentSegmentName === 'twelveHour') {
        if (curr === '-') ; else if (curr === ' ') {
          setCurrentSelectionStart(splitInput.length);
          setCurrentSelectionEnd(splitInput.length);
        } else if (curr === ':') {
          setLastSelectionStartEnd();
        } else {
          setCurrentSelectionEnd(splitInput.length);
        }
      } else if (isDelimiter) {
        setLastSelectionStartEnd();
      } else {
        setCurrentSelectionEnd(splitInput.length);
      }
    }
    return acc;
  }, initialCursorPositions);
  return positions;
};
var getIsMultipleSegmentsSelected = function getIsMultipleSegmentsSelected(cursorPosition, segmentCursorPositions) {
  var startSegment, endSegment;

  // eslint-disable-next-line no-restricted-syntax
  for (var segment in segmentCursorPositions) {
    var segmentStart = segmentCursorPositions[segment].selectionStart;
    var segmentEnd = segmentCursorPositions[segment].selectionEnd;
    var cursorStart = cursorPosition.selectionStart;
    var cursorEnd = cursorPosition.selectionEnd;
    if (cursorStart <= segmentEnd && cursorStart >= segmentStart && !startSegment) {
      startSegment = segment;
    }
    if (cursorEnd >= segmentStart && cursorEnd <= segmentEnd && !endSegment) {
      endSegment = segment;
    }
    if (cursorStart === segmentEnd && cursorEnd === segmentEnd + 1) {
      startSegment = "".concat(segment, "-delimiter");
      endSegment = "".concat(segment, "-delimiter");
    }
  }
  return startSegment !== endSegment;
};
var getIsRemovingDelimiter = function getIsRemovingDelimiter(cursorPosition, segmentCursorPositions, key) {
  var cursorStart = cursorPosition.selectionStart;
  var cursorEnd = cursorPosition.selectionEnd;
  var isBackspace = key.toLowerCase() === 'backspace';
  var isDelete = key.toLowerCase() === 'delete';
  var segmentPositions = Object.values(segmentCursorPositions);
  var getIsEntireSegmentSelected = function getIsEntireSegmentSelected(selectionStart, selectionEnd) {
    return cursorStart === selectionStart && cursorEnd === selectionEnd && selectionStart !== selectionEnd;
  };
  if (!isDelete && !isBackspace) {
    return false;
  }
  return segmentPositions.some(function (currentSegmentPositions, index) {
    var segmentStart = currentSegmentPositions.selectionStart;
    var segmentEnd = currentSegmentPositions.selectionEnd;
    if (getIsEntireSegmentSelected(segmentStart, segmentEnd)) {
      return false;
    }
    if (isBackspace) {
      var _segmentPositions;
      var cursorIsInDateTimeDelimiter = cursorStart < segmentStart && cursorStart > ((_segmentPositions = segmentPositions[index - 1]) === null || _segmentPositions === void 0 ? void 0 : _segmentPositions.selectionEnd);
      return cursorStart === segmentStart || cursorEnd === segmentStart || cursorIsInDateTimeDelimiter;
    } else {
      var _segmentPositions2;
      var _cursorIsInDateTimeDelimiter = cursorStart > segmentEnd && cursorStart < ((_segmentPositions2 = segmentPositions[index + 1]) === null || _segmentPositions2 === void 0 ? void 0 : _segmentPositions2.selectionStart);
      return cursorStart === segmentEnd || cursorEnd === segmentEnd || _cursorIsInDateTimeDelimiter;
    }
  });
};
var getCurrentSegment = function getCurrentSegment(cursorPosition, segmentCursorPositions) {
  var cursorStart = cursorPosition.selectionStart;
  var cursorEnd = cursorPosition.selectionEnd;

  // eslint-disable-next-line no-restricted-syntax
  for (var segment in segmentCursorPositions) {
    var segmentPositions = segmentCursorPositions[segment];
    if ( cursorStart >= segmentPositions.selectionStart && cursorEnd <= segmentPositions.selectionEnd) {
      return segment;
    }
  }
};
var getShouldAllowKeydown = function getShouldAllowKeydown(segmentName, event, isCursorAtEndOfInput) {
  var language = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : LOCALE_CONSTANTS.EN;
  var key = event.key,
    ctrlKey = event.ctrlKey,
    metaKey = event.metaKey;
  var value = event.target.value;
  var inputSegments = getCorrectedSegmentsFromInput(value, language);
  var segmentValue = inputSegments[segmentName];
  var arrowKeys = ['ArrowLeft', 'ArrowDown', 'ArrowRight', 'ArrowUp'];
  var isSelectAll = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'a' && (ctrlKey || metaKey);
  var isPaste = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'v' && (ctrlKey || metaKey);
  var isCopy = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'c' && (ctrlKey || metaKey);
  var isDelimiter = key.match(dateDelimiterRegex);
  var isEnteringDelimiterFirst = !inputSegments[segmentName] && key.match(endsWithDateDelimiterRegex);
  var isCursorSelected = !segmentName;
  if (isCursorSelected && isCursorAtEndOfInput || arrowKeys.includes(key) || isCopy || isPaste || isSelectAll) {
    return true;
  }
  if (segmentName === 'period') {
    return key.match(alphaRegex);
  }
  if (isEnteringDelimiterFirst) {
    return false;
  }
  if (segmentName === 'year' && isDelimiter) {
    return segmentValue.length === 4 && isCursorAtEndOfInput;
  }
  var validInputRegex = isCursorAtEndOfInput ? /[0-9]|:|\.|\/|\s|-/g : numericRegex;
  return key.match(validInputRegex);
};
var getPlaceholderText = function getPlaceholderText(inputValue) {
  var language = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : LOCALE_CONSTANTS.EN;
  var placeholder = '';
  var inputSegments = getCorrectedSegmentsFromInput(inputValue, language);
  if (!getIsNewInput(inputSegments, language)) {
    return '';
  }
  var endsWithDelimiter = inputValue.match(endsWithDelimiterRegex);
  var segmentNames = Object.keys(inputSegments);
  var lastSegment = segmentNames.reduce(function (acc, curr, index) {
    var currentSegmentValue = inputSegments[segmentNames[index]];
    var nextSegmentValue = inputSegments[segmentNames[index + 1]];
    if (!acc && currentSegmentValue && !nextSegmentValue) {
      acc = curr;
    }
    return acc;
  }, '');
  var placeholderValues;
  if (language === LOCALE_CONSTANTS.EN) {
    placeholderValues = {
      month: {
        value: 'm',
        length: 2,
        delimiter: '/'
      },
      day: {
        value: 'd',
        length: 2,
        delimiter: '/'
      },
      year: {
        value: 'y',
        length: 4,
        delimiter: ' - '
      },
      twelveHour: {
        value: 'h',
        length: 2,
        delimiter: ':'
      },
      minute: {
        value: 'm',
        length: 2,
        delimiter: ''
      },
      period: null
    };
  } else {
    placeholderValues = {
      day: {
        value: 'd',
        length: 2,
        delimiter: '/'
      },
      month: {
        value: 'm',
        length: 2,
        delimiter: '/'
      },
      year: {
        value: 'y',
        length: 4,
        delimiter: ' - '
      },
      twelveHour: {
        value: 'h',
        length: 2,
        delimiter: ':'
      },
      minute: {
        value: 'm',
        length: 2,
        delimiter: ''
      },
      period: null
    };
  }

  // eslint-disable-next-line no-restricted-syntax
  for (var segment in inputSegments) {
    var segmentObj = placeholderValues[segment];
    if (!segmentObj) {
      continue;
    }
    var value = segmentObj.value,
      length = segmentObj.length,
      delimiter = segmentObj.delimiter;
    var isLastSegment = lastSegment === segment;
    var segmentValue = inputSegments[segment];
    var noSegment = segmentObj && !segmentValue;
    var partialSegment = segmentObj && (segmentValue === null || segmentValue === void 0 ? void 0 : segmentValue.length) < (segmentObj === null || segmentObj === void 0 ? void 0 : segmentObj.length);
    var fullSegment = segmentObj && (segmentValue === null || segmentValue === void 0 ? void 0 : segmentValue.length) === (segmentObj === null || segmentObj === void 0 ? void 0 : segmentObj.length);
    if (noSegment) {
      var valueToAdd = "".concat(value.repeat(length)).concat(delimiter);
      placeholder += valueToAdd;
    } else if (partialSegment) {
      var _valueToAdd = "".concat(value.repeat(length - segmentValue.length)).concat(delimiter);
      placeholder += _valueToAdd;
    } else if (fullSegment && !endsWithDelimiter && isLastSegment) {
      placeholder += delimiter;
    } else if (fullSegment && endsWithDelimiter && segment === 'year' && isLastSegment) {
      if (inputValue.endsWith(' - ')) {
        placeholder += '';
      } else if (inputValue.endsWith(' -')) {
        placeholder += ' ';
      } else if (inputValue.endsWith(' ')) {
        placeholder += '- ';
      }
    }
  }
  return placeholder;
};

var usePrevious = function usePrevious(value) {
  var ref = useRef();
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};
var focusElement = function focusElement(className) {
  var element = document.querySelector(".".concat(className));
  if (element) {
    element.focus();
  }
};
var areDatesEqual = function areDatesEqual(dateA, dateB) {
  return (dateA === null || dateA === void 0 ? void 0 : dateA.getTime()) === (dateB === null || dateB === void 0 ? void 0 : dateB.getTime());
};
var AmplifyDateInput = function AmplifyDateInput(_ref) {
  var calendarPosition = _ref.calendarPosition,
    defaultTime = _ref.defaultTime,
    defaultDate = _ref.defaultDate,
    onDateChange = _ref.onDateChange,
    onCalendarOpen = _ref.onCalendarOpen,
    onValidation = _ref.onValidation,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    showCalendar = _ref.showCalendar,
    showValidation = _ref.showValidation,
    shouldForceValidation = _ref.shouldForceValidation,
    shouldForceClearInput = _ref.shouldForceClearInput,
    locale$2 = _ref.locale;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    cursorPosition = _useState2[0],
    setCursorPosition = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    date = _useState4[0],
    setDate = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    initialCalendarDate = _useState6[0],
    setInitialCalendarDate = _useState6[1];
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    inputValue = _useState8[0],
    setInputValue = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isCalendarInitialized = _useState10[0],
    setIsCalendarInitialized = _useState10[1];
  var _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isCalendarOpen = _useState12[0],
    setIsCalendarOpen = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isDelete = _useState14[0],
    setIsDelete = _useState14[1];
  var _useState15 = useState(!showValidation),
    _useState16 = _slicedToArray(_useState15, 2),
    isInputValid = _useState16[0],
    setIsInputValid = _useState16[1];
  var _useState17 = useState(true),
    _useState18 = _slicedToArray(_useState17, 2),
    isNewInput = _useState18[0],
    setIsNewInput = _useState18[1];
  var _useState19 = useState(locale$2 === LOCALE_CONSTANTS.EN ? 'mm/dd/yyyy - hh:mm' : 'dd/mm/yyyy - hh:mm'),
    _useState20 = _slicedToArray(_useState19, 2),
    placeholder = _useState20[0],
    setPlaceholder = _useState20[1];
  var _useState21 = useState(0),
    _useState22 = _slicedToArray(_useState21, 2),
    placeholderLeft = _useState22[0],
    setPlaceholderLeft = _useState22[1];
  var _useState23 = useState(0),
    _useState24 = _slicedToArray(_useState23, 2),
    popoverHeight = _useState24[0],
    setPopoverHeight = _useState24[1];
  var _useState25 = useState(),
    _useState26 = _slicedToArray(_useState25, 2),
    shownDate = _useState26[0],
    setShownDate = _useState26[1];
  var prevDefaultDate = usePrevious(defaultDate);
  var datePanelButtonRef = useRef();
  var datePanelRef = useRef();
  var inputRef = useRef();
  var shadowInputRef = useRef();
  var getInitialDate = function getInitialDate(minDate) {
    var hours = defaultTime.hours,
      minutes = defaultTime.minutes,
      seconds = defaultTime.seconds;
    var initialDate = new Date(minDate.valueOf());
    initialDate.setHours(hours);
    initialDate.setMinutes(minutes);
    initialDate.setSeconds(seconds);
    return initialDate;
  };
  var initializeDate = function initializeDate() {
    if (!defaultDate) {
      var initialDate = getInitialDate(minDate);
      setInitialCalendarDate(initialDate);
    } else {
      setInputValue(mapDateToInput(defaultDate, locale$2));
      setDate(defaultDate);
    }
  };
  useEffect(function () {
    initializeDate();
    if (datePanelRef !== null && datePanelRef !== void 0 && datePanelRef.current) {
      datePanelRef.current.addEventListener('keydown', keydownListener);
      window.addEventListener('resize', updatePopoverHeightOnResize);
      setPopoverHeight(datePanelRef.current.offsetHeight);
    }
    document.addEventListener('mousedown', onClickOutside);
    return function () {
      if (datePanelRef !== null && datePanelRef !== void 0 && datePanelRef.current) {
        datePanelRef.current.removeEventListener('keydown', keydownListener);
        window.removeEventListener('resize', updatePopoverHeightOnResize);
      }
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, []);
  useEffect(function () {
    var defaultDateChanged = !areDatesEqual(defaultDate, prevDefaultDate);
    if (defaultDateChanged) {
      setDate(defaultDate);
      if (!defaultDate) {
        // prevents the default calendar date from populating the input field
        setIsCalendarInitialized(false);
      }
    }
  }, [defaultDate]);
  useEffect(function () {
    if (date) {
      var isDateWithinRange;
      if (locale$2 === LOCALE_CONSTANTS.ES) {
        isDateWithinRange = true;
      } else {
        isDateWithinRange = date >= minDate && date <= maxDate;
      }
      setIsInputValid(isDateWithinRange);
      onDateChange(date);
      handleShownDateChange(date);
      setInputValue(mapDateToInput(date, locale$2));
    } else {
      setIsInputValid(true);
      onDateChange(null);
      handleShownDateChange(minDate);
      setInputValue('');
    }
  }, [date, locale$2]);
  useEffect(function () {
    onCalendarOpen(isCalendarOpen);
    if (isCalendarOpen) {
      handleShownDateChange(date);
    } else if (!defaultDate) {
      setIsCalendarInitialized(false);
    }
  }, [isCalendarOpen]);
  useEffect(function () {
    handleShownDateChange(shownDate);
  }, [minDate, maxDate]);
  useEffect(function () {
    if (minDate && !inputValue && !defaultDate) {
      var initialDate = getInitialDate(minDate);
      setInitialCalendarDate(initialDate);
    }
  }, [minDate]);
  useEffect(function () {
    if (!inputValue) {
      setIsInputValid(!showValidation);
    }
  }, [showValidation]);
  useEffect(function () {
    onValidation(isInputValid, inputValue);
  }, [isInputValid, inputValue]);
  useEffect(function () {
    if (showCalendar !== isCalendarOpen) {
      setIsCalendarOpen(showCalendar);
    }
  }, [showCalendar]);
  useEffect(function () {
    if (isNewInput) {
      var _shadowInputRef$curre;
      var width = (shadowInputRef === null || shadowInputRef === void 0 || (_shadowInputRef$curre = shadowInputRef.current) === null || _shadowInputRef$curre === void 0 ? void 0 : _shadowInputRef$curre.offsetWidth) || 0;
      setPlaceholderLeft(width + 8);
    }
    var placeholderText = getPlaceholderText(inputValue, locale$2);
    setPlaceholder(placeholderText);
  }, [inputValue, locale$2]);
  useEffect(function () {
    if (shouldForceValidation) {
      handleUserInput(inputValue);
    }
  }, [shouldForceValidation]);
  useEffect(function () {
    if (shouldForceClearInput) {
      setInputValue('');
    }
  }, [shouldForceClearInput]);
  var keydownListener = function keydownListener(e) {
    var classNames = {
      day: 'rdrDay',
      hourInput: 'time-picker-hour',
      nextMonthButton: 'rdrNextButton',
      startOfMonth: 'rdrDayStartOfMonth',
      today: 'rdrDayToday'
    };
    var key = e.key,
      shiftKey = e.shiftKey;
    var activeFocus = document.activeElement;
    var isCurrentMonth = !!document.querySelector('.' + classNames.today);
    var isDayFocused = activeFocus.classList.contains(classNames.day);
    var isHourFocused = activeFocus.classList.contains(classNames.hourInput);
    var isNextFocused = activeFocus.classList.contains(classNames.nextMonthButton);
    var arrowKeys = ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'];
    if (key === 'Escape') {
      closeCalendarOnEscape(e);
    } else if (isDayFocused && arrowKeys.includes(key)) {
      e.preventDefault();
      handleArrowKey(key);
    } else if (key === 'Tab') {
      if (isNextFocused && !shiftKey) {
        e.preventDefault();
        var dayToFocus = isCurrentMonth ? classNames.today : classNames.startOfMonth;
        focusElement(dayToFocus);
      } else if (isDayFocused) {
        e.preventDefault();
        if (shiftKey) {
          focusElement(classNames.nextMonthButton);
        } else {
          focusElement(classNames.hourInput);
        }
      } else if (isHourFocused && shiftKey) {
        e.preventDefault();
        var _dayToFocus = isCurrentMonth ? classNames.today : classNames.startOfMonth;
        focusElement(_dayToFocus);
      }
    }
  };
  var closeCalendarOnEscape = function closeCalendarOnEscape(e) {
    var key = e.key;
    if (key === 'Escape') {
      setIsCalendarOpen(false);
      datePanelButtonRef.current.focus();
    }
  };
  var handleArrowKey = function handleArrowKey(key) {
    var incrementor = {
      ArrowDown: 7,
      ArrowUp: -7,
      ArrowRight: 1,
      ArrowLeft: -1
    };
    var activeFocus = document.activeElement;
    if (activeFocus) {
      var days = document.getElementsByClassName('rdrDay');
      var activeFocusIndex = Array.from(activeFocus.parentNode.children).indexOf(activeFocus);
      var nextFocus = days[activeFocusIndex + incrementor[key]];
      var isCurrentMonth = nextFocus ? !nextFocus.classList.contains('rdrDayPassive') : false;
      if (nextFocus && isCurrentMonth) {
        nextFocus.focus();
      }
    }
  };
  var onClickOutside = function onClickOutside(e) {
    var _inputRef$current, _datePanelRef$current;
    var clickedInput = inputRef === null || inputRef === void 0 || (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.contains(e.target);
    var clickedDatePanel = datePanelRef === null || datePanelRef === void 0 || (_datePanelRef$current = datePanelRef.current) === null || _datePanelRef$current === void 0 ? void 0 : _datePanelRef$current.contains(e.target);
    if (!clickedInput && !clickedDatePanel) {
      setIsCalendarOpen(false);
    }
  };
  var getCalendarDate = function getCalendarDate() {
    // date may not be defined if a default date was not provided or if the input field was erased
    // initialCalendarDate should be the minDate combined with the default time
    if (date) {
      return date;
    } else {
      return initialCalendarDate;
    }
  };

  // this runs whenever DateRangeSetter date changes, even when we are initializing the shown date in the calendar and `date` is undefined
  var handleCalendarDateChange = function handleCalendarDateChange(calendarDate) {
    if (!isCalendarInitialized) {
      setIsCalendarInitialized(true);
    } else {
      setDate(calendarDate);
    }
  };
  var handleOnChange = function handleOnChange(e) {
    var value = e.target.value;
    handleUserInput(value);
  };
  var handleUserInput = function handleUserInput(userInput) {
    var inputSegments = getCorrectedSegmentsFromInput(userInput, locale$2);
    var isMissingSegment = getIsMissingSegment(inputSegments);
    var isSegmentsValid = getIsSegmentsValid(inputSegments);
    var newInput = getIsNewInput(inputSegments, locale$2);
    setIsNewInput(newInput);
    var anySegmentHasTooManyDigits = getHasTooManyDigits(inputSegments);
    var anySegmentHasInvalidCharacters = getHasInvalidCharacters(inputSegments);
    var date = !newInput && getIsValidPeriod(inputSegments.period) && mapInputSegmentsToDate(inputSegments);
    var isInputDateValid = date && getIsValidDate(date);
    var shouldAutofillToNextSegment = getShouldAutofillToNextSegment(inputSegments) && newInput;
    if (!userInput) {
      setInputValue('');
      setIsInputValid(true);
      return;
    } else if (isMissingSegment && !newInput) {
      setInputValue(userInput);
      setIsInputValid(false);
      return;
    } else if (isDelete) {
      setInputValue(userInput);
      setIsInputValid(isSegmentsValid);
      return;
    } else if (anySegmentHasTooManyDigits || anySegmentHasInvalidCharacters) {
      // by the time we get to this point, the new character has already been put in to the input field
      // if we want to prevent users from entering too many characters per segment, we have to reset the input value
      // but this results in rerendering the input and the cursor jumps to the end
      inputRef.current.value = inputValue;
      inputRef.current.selectionStart = cursorPosition.selectionStart;
      inputRef.current.selectionEnd = cursorPosition.selectionEnd;
      return;
    } else if (shouldAutofillToNextSegment) {
      var autofillSegments = getAutofillInputSegments(inputSegments, locale$2);
      var autofillValue = getAutofillInputValue(autofillSegments, locale$2);
      setIsInputValid(isSegmentsValid);
      setInputValue(autofillValue);
    } else if (isInputDateValid && isSegmentsValid) {
      setDate(date);
    } else if (newInput) {
      setInputValue(userInput);
      setIsInputValid(isSegmentsValid);
    } else {
      setInputValue(formatInputValue(inputSegments, locale$2));
      setIsInputValid(false);
    }
  };
  var handleOnBlur = function handleOnBlur() {
    var inputSegments = inputValue && getCorrectedSegmentsFromInput(inputValue, locale$2);
    var newInput = inputValue && getIsNewInput(inputSegments, locale$2);
    if (newInput) {
      var _inputSegments = getCorrectedSegmentsFromInput(inputValue, locale$2);
      var month = _inputSegments.month,
        day = _inputSegments.day,
        year = _inputSegments.year,
        twelveHour = _inputSegments.twelveHour,
        minute = _inputSegments.minute,
        period = _inputSegments.period;
      var hasDate = month && day && year && (year === null || year === void 0 ? void 0 : year.length) === 4;
      var isMissingTime = !twelveHour && !minute && !period;
      if (hasDate && isMissingTime) {
        _inputSegments.twelveHour = get12HourFrom24Hour(defaultTime.hours);
        _inputSegments.minute = defaultTime.minutes;
        _inputSegments.period = getCurrentPeriod(getIsAM(defaultTime.hours)).toUpperCase();
        setDate(mapInputSegmentsToDate(_inputSegments));
      }
    }
  };
  var handleOnKeyDown = function handleOnKeyDown(e) {
    e.persist();
    var key = e.key,
      ctrlKey = e.ctrlKey,
      metaKey = e.metaKey;
    var _e$target = e.target,
      selectionStart = _e$target.selectionStart,
      selectionEnd = _e$target.selectionEnd,
      value = _e$target.value;
    var cursorPosition = {
      selectionStart: selectionStart,
      selectionEnd: selectionEnd
    };
    setCursorPosition(cursorPosition);
    var escapeKeyPressed = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'escape';
    var tabKeyPressed = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'tab';
    var segmentCursorPositions = getCursorPositionsFromInput(value, locale$2);
    var isPeriodLastSegment = segmentCursorPositions.period.selectionStart;
    var isCopy = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'c' && (ctrlKey || metaKey);

    // want length - 1 allow 'delete' of delimiters at the end of the input
    var isCursorAtEndOfInput = isPeriodLastSegment ? selectionEnd === value.length : selectionEnd === value.length || selectionEnd === value.length - 1;
    var isMultipleSegmentsSelected = getIsMultipleSegmentsSelected(cursorPosition, segmentCursorPositions);
    var isDelete = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'delete';
    var isBackspace = (key === null || key === void 0 ? void 0 : key.toLowerCase()) === 'backspace';
    setIsDelete(isDelete || isBackspace);
    if (isDelete || isBackspace) {
      var isRemovingDelimiter = getIsRemovingDelimiter(cursorPosition, segmentCursorPositions, key);
      if (isMultipleSegmentsSelected && !isCursorAtEndOfInput) {
        var selected = value.slice(selectionStart, selectionEnd);
        var segmentsRemoved = selected.replace(/[0-9a-zA-Z]/g, '');
        var newValue = value.replace(selected, segmentsRemoved);
        e.preventDefault();
        handleUserInput(newValue);
        setIsInputValid(false);
      } else if (isRemovingDelimiter && !isCursorAtEndOfInput) {
        e.preventDefault();
        return;
      }
    } else {
      var currentSegment = getCurrentSegment(cursorPosition, segmentCursorPositions);
      var shouldAllow = getShouldAllowKeydown(currentSegment, e, isCursorAtEndOfInput, locale$2);
      var shouldPrevent = !shouldAllow || isMultipleSegmentsSelected && !isCursorAtEndOfInput && !isCopy;
      if (shouldPrevent) {
        e.preventDefault();
        return;
      }
    }
    if (escapeKeyPressed || tabKeyPressed) {
      setIsCalendarOpen(false);
    }
  };
  var handleOnPaste = function handleOnPaste(e) {
    var pastedInput = e.clipboardData.getData('text');
    var isValid = getIsFullInputValid(pastedInput, locale$2);
    if (!isValid) {
      e.preventDefault();
    }
  };
  var focusOnNextMonthButton = function focusOnNextMonthButton() {
    setTimeout(function () {
      var calendarNextButton = document.querySelector('.rdrNextButton');
      if (calendarNextButton) {
        calendarNextButton.focus();
      }
    });
  };
  var handleCalendarButtonClick = function handleCalendarButtonClick() {
    setIsCalendarOpen(true);
    focusOnNextMonthButton();
  };
  var handleFocus = function handleFocus() {
    setIsCalendarOpen(true);
  };
  var getPopoverStyle = function getPopoverStyle(position) {
    var _inputRef$current2;
    if (!inputRef.current || !datePanelButtonRef.current || !(inputRef !== null && inputRef !== void 0 && (_inputRef$current2 = inputRef.current) !== null && _inputRef$current2 !== void 0 && _inputRef$current2.offsetParent)) {
      return {};
    }
    var inputValidationLabel = document.querySelector('div.start-date-input-wrapper > div > span');
    var inputValidationLabelHeight = inputValidationLabel ? inputValidationLabel.offsetHeight : 0;
    switch (position) {
      case 'top':
        return {
          right: "".concat(inputRef.current.offsetParent.clientWidth - inputRef.current.offsetWidth - datePanelButtonRef.current.offsetWidth, "px")
        };
      case 'bottom':
        return {
          bottom: "-".concat(datePanelRef.current.offsetHeight, "px"),
          right: "".concat(inputRef.current.offsetParent.clientWidth - inputRef.current.offsetWidth - datePanelButtonRef.current.offsetWidth, "px"),
          transform: "rotate(180deg) translateY(calc(".concat(popoverHeight === 0 ? datePanelRef.current.offsetHeight : popoverHeight, "px + 100%))"),
          pseudoElBorderWidth: '10px'
        };
      case 'left':
        return {
          bottom: "calc(-30px + ".concat(inputValidationLabelHeight, "px)"),
          right: "calc(".concat(inputRef.current.offsetParent.clientWidth, "px + 18%)"),
          transform: 'rotate(-90deg)',
          pseudoElTop: '85%',
          pseudoElLeft: '100%'
        };
      default:
        return {
          right: "".concat(inputRef.current.offsetParent.clientWidth - inputRef.current.offsetWidth - datePanelButtonRef.current.offsetWidth, "px")
        };
    }
  };

  /* this function is called on window resize and results in recalcutaling the style object for popover element; this prevents :before and :after pointy pseudo-element floating away from DatePanelDiv */
  var updatePopoverHeightOnResize = function updatePopoverHeightOnResize() {
    if ((datePanelRef === null || datePanelRef === void 0 ? void 0 : datePanelRef.current.offsetHeight) !== popoverHeight) {
      setPopoverHeight(datePanelRef.current.offsetHeight);
    }
  };
  var getDateValues = function getDateValues(date) {
    return [date.getMonth(), date.valueOf(), date.getFullYear()];
  };
  var handleShownDateChange = function handleShownDateChange(shownDate) {
    var _datePanelRef$current2, _datePanelRef$current3;
    if (!shownDate) {
      return;
    }
    setShownDate(shownDate);
    var prev = datePanelRef === null || datePanelRef === void 0 || (_datePanelRef$current2 = datePanelRef.current) === null || _datePanelRef$current2 === void 0 ? void 0 : _datePanelRef$current2.querySelector('.rdrPprevButton');
    var next = datePanelRef === null || datePanelRef === void 0 || (_datePanelRef$current3 = datePanelRef.current) === null || _datePanelRef$current3 === void 0 ? void 0 : _datePanelRef$current3.querySelector('.rdrNextButton');
    var addDisabled = function addDisabled(el) {
      (el === null || el === void 0 ? void 0 : el.classList) && el.classList.add('disabled');
    };
    var removeDisabled = function removeDisabled(el) {
      (el === null || el === void 0 ? void 0 : el.classList) && el.classList.remove('disabled');
    };
    var _getDateValues = getDateValues(maxDate),
      _getDateValues2 = _slicedToArray(_getDateValues, 3),
      maxMonth = _getDateValues2[0],
      maxValue = _getDateValues2[1],
      maxYear = _getDateValues2[2];
    var _getDateValues3 = getDateValues(minDate),
      _getDateValues4 = _slicedToArray(_getDateValues3, 3),
      minMonth = _getDateValues4[0],
      minValue = _getDateValues4[1],
      minYear = _getDateValues4[2];
    var _getDateValues5 = getDateValues(shownDate),
      _getDateValues6 = _slicedToArray(_getDateValues5, 3),
      shownMonth = _getDateValues6[0],
      shownValue = _getDateValues6[1],
      shownYear = _getDateValues6[2];
    var isMaxAfterShown = shownValue < maxValue;
    var isMaxBeforeShown = shownValue > maxValue;
    var isMaxMonth = shownYear === maxYear && shownMonth === maxMonth;
    var isMinAfterShown = shownValue < minValue;
    var isMinBeforeShown = shownValue > minValue;
    var isMinMonth = shownYear === minYear && shownMonth === minMonth;
    if ((isMinMonth || isMinAfterShown) && !isMaxBeforeShown) {
      addDisabled(prev);
    } else if ((isMaxMonth || isMaxBeforeShown) && !isMinAfterShown) {
      addDisabled(next);
    } else {
      isMinBeforeShown && removeDisabled(prev);
      isMaxAfterShown && removeDisabled(next);
    }
  };
  var getLangaugeFormatObj = function getLangaugeFormatObj(language) {
    switch (language) {
      case LOCALE_CONSTANTS.ES:
        {
          return locale$1;
        }
      default:
        {
          return locale;
        }
    }
  };
  return /*#__PURE__*/React.createElement(WrapperDiv, {
    className: "amplify-date-input amp-styles ".concat(isCalendarOpen ? 'calendarIsOpen' : '')
  }, /*#__PURE__*/React.createElement(DatePanelDiv, {
    ref: datePanelRef,
    className: "date-panel-popover ".concat(isCalendarOpen ? '' : 'hidden'),
    styledComponentsProps: getPopoverStyle(calendarPosition)
  }, getCalendarDate() ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    className: "close-date-panel-btn",
    "aria-label": "Close date panel",
    onClick: function onClick() {
      return setIsCalendarOpen(false);
    },
    type: "button"
  }, /*#__PURE__*/React.createElement("i", {
    className: "icon-v2-close"
  })), /*#__PURE__*/React.createElement("div", {
    className: "calendar-arrow-bottom"
  }), /*#__PURE__*/React.createElement("div", {
    className: "calendar-arrow-top"
  }), isCalendarOpen && /*#__PURE__*/React.createElement(DateRangeSetter, {
    date: getCalendarDate(),
    defaultTime: defaultTime,
    useDefaultTime: !defaultDate,
    handleChange: handleCalendarDateChange,
    handleShownDateChange: handleShownDateChange,
    isRangePicker: false,
    maxDate: maxDate,
    minDate: minDate,
    months: 1,
    showTimePicker: true,
    useDateFromPropsOnly: true,
    useDropdown: false,
    fixedHeight: true,
    locale: getLangaugeFormatObj(locale$2)
  })) : null), /*#__PURE__*/React.createElement(InputDiv, {
    className: "input-wrapper",
    placeholder: placeholder,
    placeholderLeft: placeholderLeft
  }, /*#__PURE__*/React.createElement("input", {
    className: "formatted-date-input ".concat(isInputValid ? '' : 'invalid'),
    value: inputValue,
    "aria-label": "date-input",
    onChange: function onChange(e) {
      return handleOnChange(e);
    },
    onFocus: function onFocus() {
      return handleFocus();
    },
    onKeyDown: function onKeyDown(e) {
      return handleOnKeyDown(e);
    },
    onBlur: function onBlur() {
      return handleOnBlur();
    },
    onPaste: function onPaste(e) {
      return handleOnPaste(e);
    },
    ref: inputRef
  }), /*#__PURE__*/React.createElement("button", {
    "aria-label": "Open date panel",
    ref: datePanelButtonRef,
    className: "btn btn-ghost open-date-panel-btn",
    onClick: function onClick() {
      return handleCalendarButtonClick();
    },
    type: "button"
  }, /*#__PURE__*/React.createElement("i", {
    className: "icon-v2-calendar"
  })), /*#__PURE__*/React.createElement("div", {
    ref: shadowInputRef,
    className: "shadow-date-input"
  }, inputValue)), !isInputValid && /*#__PURE__*/React.createElement("span", {
    className: "input-label-validation"
  }, "Invalid entry"));
};
AmplifyDateInput.defaultProps = {
  calendarPosition: 'top',
  defaultDate: null,
  defaultTime: {
    hours: 23,
    minutes: 59,
    seconds: 59
  },
  maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
  minDate: new Date(),
  onCalendarOpen: function onCalendarOpen() {},
  onValidation: function onValidation() {},
  showCalendar: false,
  showValidation: false,
  shouldForceValidation: false,
  shouldForceClearInput: false,
  locale: LOCALE_CONSTANTS.EN
};
AmplifyDateInput.propTypes = {
  calendarPosition: PropTypes.string,
  defaultDate: PropTypes.instanceOf(Date),
  defaultTime: PropTypes.shape({
    hours: PropTypes.number,
    minutes: PropTypes.number,
    seconds: PropTypes.number
  }),
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  onCalendarOpen: PropTypes.func,
  onDateChange: PropTypes.func.isRequired,
  onValidation: PropTypes.func,
  showCalendar: PropTypes.bool,
  showValidation: PropTypes.bool,
  shouldForceValidation: PropTypes.bool,
  shouldForceClearInput: PropTypes.bool,
  locale: PropTypes.oneOf([LOCALE_CONSTANTS.EN, LOCALE_CONSTANTS.ES])
};

export default AmplifyDateInput;
